import { createPaidVideoRecord, deletePaidVideoRecord, fetchOwnPaidVideos } from "./lib/paidVideoApi";
import { signedPost as signedPostPaymentPreview } from "./lib/signedRequest";
import React, { useEffect, useState, useMemo } from "react";
import {
  ShieldCheck, KeyRound, RefreshCw, Copy, Check, ChevronLeft,
  AlertTriangle, X, LogOut,
  MessageCircle, Lock, Image as ImageIcon, Radio, Download,
  Users,
  UserCheck,
  FileText,
  Heart,
  ThumbsDown,
  Share2,
  ArrowRight} from "lucide-react";
import {
  generateMnemonicWords,
  deriveKeypair,
  isValidMnemonic,
  saveIdentity,
  buildBackupBlob,
} from "./lib/identity";
import { fetchPostsByAuthor, generateAiAvatar, upsertProfile, uploadAvatarBlob, uploadPostMedia, requestMediaModeration, requestProfileCoverModeration, publishVoicePost, publishProfilePost } from "./lib/postsApi.js";
import { fetchMyNetwork, fetchProfile, updateProfileBio, updateProfileCover } from "./lib/socialApi.js";
import { devDebug } from "./lib/devLog";
import {
  createStory,
  fetchStoriesByIdentity,
  deleteStory,
} from "./lib/storiesApi";
import {
  createProfileContent,
  fetchProfileContents,
  fetchOwnProfileContents,
  deleteProfileContent,
} from "./lib/profileContentApi";

// ============ Registrazione ============
const AVATAR_URLS = [
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=140&h=140&fit=crop&crop=faces",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=140&h=140&fit=crop&crop=faces",
];

const AVATAR_SIZE = 256;
const AVATAR_INPUT_MAX_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function canvasBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Impossibile creare l’avatar")),
      "image/webp",
      0.86,
    );
  });
}

function loadBrowserImage(url) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Immagine non leggibile"));
    image.src = url;
  });
}

async function resizeAvatarFile(file) {
  if (!AVATAR_TYPES.has(file?.type)) throw new Error("Usa un’immagine JPEG, PNG o WebP");
  if (file.size > AVATAR_INPUT_MAX_BYTES) throw new Error("L’immagine originale non può superare 5 MB");
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadBrowserImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;
    const ctx = canvas.getContext("2d");
    const side = Math.min(image.naturalWidth, image.naturalHeight);
    const sx = (image.naturalWidth - side) / 2;
    const sy = (image.naturalHeight - side) / 2;
    ctx.drawImage(image, sx, sy, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
    return { preview: canvas.toDataURL("image/webp", 0.86), blob: await canvasBlob(canvas), source: "upload" };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function generateLocalAvatar(name, gender, options = {}) {
  const seedText = `${name.trim() || "OpinioneLibera"}-${gender}-${Date.now()}-${Math.random()}`;
  let seed = 0;
  for (let i = 0; i < seedText.length; i += 1) seed = ((seed << 5) - seed + seedText.charCodeAt(i)) | 0;
  const pick = (items, offset = 0) => items[Math.abs(seed + offset) % items.length];
  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;
  const ctx = canvas.getContext("2d");
  const hueA = Math.abs(seed) % 360;
  const hueB = (hueA + 80 + (Math.abs(seed) % 90)) % 360;
  const gradient = ctx.createLinearGradient(0, 0, AVATAR_SIZE, AVATAR_SIZE);
  gradient.addColorStop(0, `hsl(${hueA} 62% 43%)`);
  gradient.addColorStop(1, `hsl(${hueB} 72% 68%)`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);

  const skin = options.skin || pick(["#f3c7a5", "#d99b73", "#b87550", "#7d4935"], 17);
  const hair = options.hair || pick(["#201813", "#4a2b1b", "#7b4a27", "#d1a35d", "#4b4b52"], 31);
  const eye = options.eye || pick(["#2e2119", "#36566e", "#436244", "#6b4a2d"], 47);

  // Capelli posteriori: corti per uomo, più lunghi per donna.
  ctx.fillStyle = hair;
  if (gender === "female") {
    ctx.beginPath();
    ctx.ellipse(128, 122, 76, 102, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.ellipse(128, 79, 67, 43, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Collo, orecchie e volto.
  ctx.fillStyle = skin;
  ctx.fillRect(108, 171, 40, 55);
  ctx.beginPath();
  ctx.ellipse(65, 126, 13, 20, 0, 0, Math.PI * 2);
  ctx.ellipse(191, 126, 13, 20, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(128, 125, gender === "female" ? 61 : 65, gender === "female" ? 78 : 75, 0, 0, Math.PI * 2);
  ctx.fill();

  // Acconciatura anteriore differenziata.
  ctx.fillStyle = hair;
  ctx.beginPath();
  if (gender === "female") {
    ctx.moveTo(68, 105);
    ctx.quadraticCurveTo(82, 41, 137, 48);
    ctx.quadraticCurveTo(191, 55, 190, 111);
    ctx.quadraticCurveTo(159, 72, 124, 78);
    ctx.quadraticCurveTo(91, 83, 68, 105);
  } else {
    ctx.moveTo(66, 100);
    ctx.quadraticCurveTo(72, 52, 125, 48);
    ctx.quadraticCurveTo(183, 48, 191, 101);
    ctx.quadraticCurveTo(160, 73, 126, 76);
    ctx.quadraticCurveTo(91, 75, 66, 100);
  }
  ctx.fill();

  // Sopracciglia, occhi, naso e bocca.
  ctx.strokeStyle = hair;
  ctx.lineWidth = gender === "female" ? 4 : 5;
  ctx.beginPath();
  ctx.moveTo(88, 112); ctx.quadraticCurveTo(102, 104, 114, 111);
  ctx.moveTo(142, 111); ctx.quadraticCurveTo(155, 104, 169, 112);
  ctx.stroke();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.ellipse(102, 124, 10, 6, 0, 0, Math.PI * 2);
  ctx.ellipse(154, 124, 10, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = eye;
  ctx.beginPath();
  ctx.arc(102, 124, 4, 0, Math.PI * 2);
  ctx.arc(154, 124, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#9b5f4c";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(128, 128); ctx.lineTo(122, 151); ctx.quadraticCurveTo(128, 155, 135, 151);
  ctx.stroke();
  ctx.strokeStyle = gender === "female" ? "#a83e52" : "#704238";
  ctx.lineWidth = gender === "female" ? 5 : 4;
  ctx.beginPath();
  ctx.moveTo(108, 171); ctx.quadraticCurveTo(128, 183, 149, 170);
  ctx.stroke();

  // Spalle e piccolo monogramma.
  ctx.fillStyle = pick(["#172554", "#3f1d2e", "#153d38", "#3b2f23"], 61);
  ctx.beginPath();
  ctx.ellipse(128, 256, 104, 64, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  const initials = (name.trim() || "OL").split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
  ctx.fillStyle = "rgba(255,255,255,.9)";
  ctx.font = "700 22px Georgia, serif";
  ctx.textAlign = "right";
  ctx.fillText(initials, 238, 238);
  return { preview: canvas.toDataURL("image/webp", 0.86), blob: await canvasBlob(canvas), source: "ai" };
}

function useCopyWords(words) {
  const [copyDone, setCopyDone] = useState(false);
  const flash = () => {
    setCopyDone(true);
    setTimeout(() => setCopyDone(false), 1500);
  };
  const copyWords = () => {
    if (!navigator.clipboard?.writeText) return flash();
    navigator.clipboard.writeText(words.join(" ")).then(flash).catch(flash);
  };
  return { copyDone, copyWords };
}

function isVerifyOk(values, words) {
  return (
    values[3].trim().toLowerCase() === words[3] &&
    values[7].trim().toLowerCase() === words[7] &&
    values[11].trim().toLowerCase() === words[11]
  );
}

function RegistrationSteps({ step, setStep, name, setName, avatarGender, setAvatarGender, avatarSelection, setAvatarSelection, verifyValues, setVerifyValues, words, onComplete, onRecover, recoveryError, recovering, mode }) {
  const { copyDone, copyWords } = useCopyWords(words);
  const verifyOk = isVerifyOk(verifyValues, words);

  if (mode === "recover") {
    return (
      <StepRecover
        onSuccess={onRecover}
        onBack={() => setStep(1)}
        error={recoveryError}
        busy={recovering}
      />
    );
  }
  if (step === 1) return <StepAgeGate onNext={() => setStep(2)} onRecover={() => setStep(0)} />;
  if (step === 2) return (
    <StepIdentity
      name={name} setName={setName}
      avatars={AVATAR_URLS} avatarGender={avatarGender} setAvatarGender={setAvatarGender} avatarSelection={avatarSelection} setAvatarSelection={setAvatarSelection}
      onNext={() => setStep(3)} onBack={() => setStep(1)}
    />
  );
  if (step === 3) return <StepWords words={words} onCopy={copyWords} copyDone={copyDone} onNext={() => setStep(4)} onBack={() => setStep(2)} />;
  if (step === 4) return (
    <StepVerify
      values={verifyValues} setValues={setVerifyValues}
      verifyOk={verifyOk} onNext={() => setStep(5)} onBack={() => setStep(3)}
    />
  );
  return <StepDone name={name} avatar={avatarSelection.preview} onEnter={() => onComplete({ name, avatar: avatarSelection.preview, avatarBlob: avatarSelection.blob, avatarSource: avatarSelection.source, avatarGender, words })} />;
}

export function RegistrationView({ onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [avatarGender, setAvatarGender] = useState("male");
  const [avatarSelection, setAvatarSelection] = useState({ preview: "", blob: null });
  const [verifyValues, setVerifyValues] = useState({ 3: "", 7: "", 11: "" });
  const [recoveryError, setRecoveryError] = useState(null);
  const [recovering, setRecovering] = useState(false);
  // Real 12-word BIP39 mnemonic generated ONCE per wizard session.
  const words = useMemo(() => generateMnemonicWords(), []);

  const handleRecover = async (inputWords) => {
    setRecoveryError(null);
    if (!isValidMnemonic(inputWords)) {
      setRecoveryError("Mnemonica non valida (checksum errato). Ricontrolla le parole.");
      return;
    }
    setRecovering(true);
    try {
      const keys = await deriveKeypair(inputWords);
      let profile = null;
      try { profile = await fetchProfile(keys.identityId); } catch (err) { /* no profile yet */ devDebug("recover: fetchProfile failed", err?.message); }
      if (!profile?.displayName || !profile?.avatar) {
        setRecoveryError("Nessun profilo completo è associato a queste 12 parole.");
        return;
      }
      const recoveredName = profile.displayName;
      const recoveredAvatar = profile.avatar;
      await saveIdentity({
        name: recoveredName,
        avatar: recoveredAvatar,
        mnemonic: inputWords.join(" "),
        publicKeyHex: keys.publicKeyHex,
        identityId: keys.identityId,
      });
      onComplete({
        name: recoveredName,
        avatar: recoveredAvatar,
        identityId: keys.identityId,
        publicKeyHex: keys.publicKeyHex,
      });
    } catch (err) {
      setRecoveryError(`Ripristino fallito: ${err?.message || "errore sconosciuto"}`);
    } finally {
      setRecovering(false);
    }
  };

  const handleComplete = async (data) => {
    const keys = await deriveKeypair(data.words);
    const identityBase = {
      name: data.name,
      mnemonic: data.words.join(" "),
      publicKeyHex: keys.publicKeyHex,
      identityId: keys.identityId,
    };
    // Save the cryptographic identity first: the protected avatar endpoint
    // needs it to mint and sign the one-shot upload token.
    let finalAvatar = data.avatar;
    await saveIdentity({ ...identityBase, avatar: finalAvatar });
    if (data.avatarSource === "ai") {
      try {
        finalAvatar = await generateAiAvatar(data.avatarGender);
        await saveIdentity({ ...identityBase, avatar: finalAvatar });
      } catch (err) {
        devDebug("AI avatar generation failed; using local fallback", err?.message);
        if (data.avatarBlob) {
          try {
            finalAvatar = await uploadAvatarBlob(data.avatarBlob);
            await saveIdentity({ ...identityBase, avatar: finalAvatar });
          } catch (uploadErr) {
            devDebug("fallback avatar upload failed", uploadErr?.message);
          }
        }
      }
    } else if (data.avatarBlob) {
      try {
        finalAvatar = await uploadAvatarBlob(data.avatarBlob);
        await saveIdentity({ ...identityBase, avatar: finalAvatar });
      } catch (err) {
        devDebug("custom avatar upload failed", err?.message);
      }
    }
    // Register the public profile so the feed shows the real pseudonym.
    // Failure is non-blocking — the app still works with local identity.
    try {
      await upsertProfile({ displayName: data.name, avatar: finalAvatar });
    } catch (err) {
      devDebug("upsertProfile after registration failed", err?.message);
    }
    onComplete({
      name: data.name,
      avatar: finalAvatar,
      identityId: keys.identityId,
      publicKeyHex: keys.publicKeyHex,
    });
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6" data-testid="registration-view" translate="yes">
      <img
        src="/you-voice-brand.png?v=20260811"
        alt="You Voice"
        className="onboarding-brand"
        data-testid="onboarding-brand"
      />
      <div className="w-full max-w-md card p-6" data-testid={`registration-step-${step}`}>
        <RegistrationSteps
          step={step} setStep={setStep}
          name={name} setName={setName}
          avatarGender={avatarGender} setAvatarGender={setAvatarGender}
          avatarSelection={avatarSelection} setAvatarSelection={setAvatarSelection}
          verifyValues={verifyValues} setVerifyValues={setVerifyValues}
          words={words}
          onComplete={handleComplete}
          onRecover={handleRecover}
          recoveryError={recoveryError}
          recovering={recovering}
          mode={step === 0 ? "recover" : "create"}
        />
      </div>
      <ProgressDots step={step === 0 ? 0 : step} total={5} />
    </div>
  );
}

function ProgressDots({ step, total }) {
  return (
    <div className="flex gap-1.5 mt-6">
      {Array.from({ length: total }).map((_, i) => (
        <div key={`dot-${i}`} className={`w-2 h-2 rounded-full ${i < step ? "bg-primary" : "bg-edge"}`} />
      ))}
    </div>
  );
}

function StepAgeGate({ onNext, onRecover }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center gap-1.5 border-2 border-primary rounded-full px-4 py-1 text-primary font-bold text-sm mb-4">18+</div>
      <h2 className="font-display font-semibold text-3xl mb-2">Piattaforma per adulti</h2>
      <p className="text-sm text-muted mb-6">Confermando qui sotto dichiari di avere almeno 18 anni e di accettare le regole di moderazione soft.</p>
      <button className="big-cta" onClick={onNext} data-testid="confirm-age-btn">
        Confermo · Ho più di 18 anni
      </button>
      <button
        className="mt-4 text-sm text-primary hover:underline font-semibold"
        onClick={onRecover}
        data-testid="recover-btn"
      >
        Ho già le 12 parole · Ripristina profilo
      </button>
      <p className="text-[11px] text-muted mt-4">Non chiediamo mai email, telefono o dati reali.</p>
    </div>
  );
}

function StepRecover({ onSuccess, onBack, error, busy }) {
  const [inputs, setInputs] = React.useState(Array(12).fill(""));
  const setWord = (i, v) => {
    const clean = v.trim().toLowerCase();
    setInputs((arr) => {
      const copy = arr.slice();
      copy[i] = clean;
      return copy;
    });
  };
  const onPaste = (i, e) => {
    const text = e.clipboardData.getData("text") || "";
    const parts = text.trim().split(/\s+/);
    if (parts.length >= 12) {
      e.preventDefault();
      setInputs(parts.slice(0, 12).map((w) => w.toLowerCase()));
    }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const j = JSON.parse(text);
      if (typeof j?.mnemonic === "string") {
        const words = j.mnemonic.trim().split(/\s+/);
        if (words.length === 12) setInputs(words.map((w) => w.toLowerCase()));
      }
    } catch (err) { devDebug("recover: backup file parse failed", err?.message); }
  };
  const allFilled = inputs.every((w) => w.length > 0);
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro">
          <ChevronLeft size={18} />
        </button>
        <h2 className="font-display font-bold text-2xl">Ripristina profilo</h2>
      </div>
      <p className="text-xs text-muted mb-4">
        Inserisci le 12 parole (o incolla la mnemonic completa in una sola casella, o carica il file di backup).
      </p>
      <div className="grid grid-cols-3 gap-2 mb-3" data-testid="recover-grid">
        {inputs.map((w, i) => (
          <div key={`recover-${i}`} className="border border-edge rounded-md px-2 py-1.5 flex items-center gap-1.5 bg-white">
            <span className="text-[10px] text-muted tabular-nums">{i + 1}</span>
            <input
              type="text"
              value={w}
              onChange={(e) => setWord(i, e.target.value)}
              onPaste={(e) => onPaste(i, e)}
              className="w-full text-sm font-mono outline-none"
              data-testid={`recover-word-${i}`}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>
        ))}
      </div>
      <label className="block text-xs text-muted mb-3 cursor-pointer">
        <span className="underline">Oppure carica il file JSON di backup</span>
        <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="recover-file" />
      </label>
      {error && (
        <div className="mb-3 text-xs text-primary flex items-center gap-2" data-testid="recover-error">
          <AlertTriangle size={12} /> {error}
        </div>
      )}
      <button
        className="big-cta"
        onClick={() => onSuccess(inputs)}
        disabled={!allFilled || busy}
        data-testid="recover-submit-btn"
      >
        {busy ? "Ripristino in corso…" : "Ripristina il mio profilo"}
      </button>
    </div>
  );
}

function StepIdentity({ name, setName, avatars, avatarGender, setAvatarGender, avatarSelection, setAvatarSelection, onNext, onBack }) {
  const [avatarError, setAvatarError] = useState("");
  const [avatarBusy, setAvatarBusy] = useState(false);

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setAvatarBusy(true);
    setAvatarError("");
    try {
      setAvatarSelection(await resizeAvatarFile(file));
    } catch (err) {
      setAvatarError(err?.message || "Impossibile caricare l’immagine");
    } finally {
      setAvatarBusy(false);
    }
  };

  const generateAvatar = async () => {
    setAvatarBusy(true);
    setAvatarError("");
    try {
      setAvatarSelection(await generateLocalAvatar(name, avatarGender));
    } catch (err) {
      setAvatarError(err?.message || "Impossibile generare l’avatar");
    } finally {
      setAvatarBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro"><ChevronLeft size={18} /></button>
        <h2 className="font-display font-semibold text-2xl">Crea il tuo profilo</h2>
      </div>
      <p className="text-xs text-muted mb-4">Usa uno pseudonimo. La tua identità reale resta protetta.</p>

      <label className="mt-1 text-sm font-semibold leading-tight text-black">Scegli come pseudonimo</label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="es. UomoLibero, VoceCritica…"
        className="mt-1 w-full border border-edge rounded-md px-3 py-2 text-sm outline-none focus:border-ink"
        data-testid="pseudonimo-input"
        maxLength={24}
      />
      <p className="text-[11px] text-muted mt-1">Modificabile ogni 7 giorni · Fino a 24 caratteri</p>

      <div className="mt-4">
        <label className="text-[11px] uppercase tracking-widest text-muted">Scegli il tipo di avatar da generare</label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {[
            { value: "male", label: "Uomo", src: avatars[0] },
            { value: "female", label: "Donna", src: avatars[1] },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => { setAvatarGender(option.value); setAvatarError(""); }}
              className={`flex items-center gap-3 border-2 rounded-lg p-2 text-left ${avatarGender === option.value ? "border-primary bg-primary/5" : "border-edge"}`}
              data-testid={`avatar-gender-${option.value}`}
              aria-pressed={avatarGender === option.value}
            >
              <img src={option.src} alt="" className="w-12 h-12 rounded-full object-cover" />
              <span className="font-semibold">{option.label}</span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted mt-2">Queste immagini indicano soltanto il tipo uomo/donna: non diventeranno l’avatar del profilo.</p>

        {avatarSelection.blob && (
          <div className="mt-3 flex items-center gap-3 border border-edge rounded-lg p-2">
            <img src={avatarSelection.preview} alt="Anteprima avatar personalizzato" className="w-16 h-16 rounded-full object-cover border-2 border-primary" />
            <span className="text-sm font-semibold">Anteprima del tuo avatar</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-3">
          <label className="brutal-btn ghost justify-center cursor-pointer" data-testid="avatar-upload-label">
            <ImageIcon size={14} /> Carica foto
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={chooseFile}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </label>
          <button
            type="button"
            className="brutal-btn ghost justify-center"
            onClick={generateAvatar}
            disabled={avatarBusy}
            data-testid="avatar-generate-btn"
          >
            <RefreshCw size={14} className={avatarBusy ? "animate-spin" : ""} />
            {avatarBusy ? "Attendi…" : `Genera ${avatarGender === "male" ? "uomo" : "donna"}`}
          </button>
        </div>
        <p className="text-[11px] text-muted mt-2">In alternativa puoi caricare una tua foto. Viene ritagliata e ridimensionata automaticamente; JPEG, PNG o WebP, massimo 5 MB.</p>
        {avatarError && <p className="text-xs text-primary mt-2" role="alert">{avatarError}</p>}
      </div>

      <button className="big-cta mt-6" disabled={name.trim().length < 3 || avatarBusy || !avatarSelection.blob} onClick={onNext} data-testid="identity-next-btn">
        Continua
      </button>
    </div>
  );
}

function StepWords({ words, onCopy, copyDone, onNext, onBack }) {
  const downloadBackup = () => {
    const blob = buildBackupBlob({ mnemonic: words.join(" ") });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "opinione-libera-backup.json";
    a.click();
    URL.revokeObjectURL(url);
  };  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro"><ChevronLeft size={18} /></button>
        <h2 className="font-display font-semibold text-2xl">Le tue 12 parole</h2>
      </div>
      <div className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/30 rounded-md mb-4">
        <ShieldCheck size={20} className="text-primary shrink-0 mt-0.5" />
        <p className="text-xs text-ink">
          Queste 12 parole sono <strong>l'unica via</strong> per recuperare il tuo profilo su altri dispositivi.
          Salvale bene: nessuno, nemmeno noi, può recuperarle al posto tuo.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="bip39-grid" data-no-translate="true" translate="no">
        {words.map((w, i) => (
          <div key={`word-${i}`} className="border border-edge rounded-md px-2 py-1.5 flex items-center gap-1.5 bg-white" data-testid={`bip39-word-${i}`}>
            <span className="text-[10px] text-muted tabular-nums">{i + 1}</span>
            <span className="text-sm font-mono font-semibold">{w}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <button className="brutal-btn ghost flex-1 justify-center" onClick={onCopy} data-testid="copy-words-btn">
          {copyDone ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> Copia parole</>}
        </button>
        <button className="brutal-btn ghost flex-1 justify-center" onClick={downloadBackup} data-testid="download-backup-btn">
          <Download size={12} /> Scarica backup
        </button>
      </div>
      <button className="big-cta mt-5" onClick={onNext} data-testid="words-next-btn">
        Ho salvato le 12 parole
      </button>
    </div>
  );
}

function StepVerify({ values, setValues, verifyOk, onNext, onBack }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-muted hover:text-ink" aria-label="Indietro"><ChevronLeft size={18} /></button>
        <h2 className="font-display font-semibold text-2xl">Verifica 3 parole</h2>
      </div>
      <p className="text-xs text-muted mb-4">Inserisci le parole richieste per confermare di averle salvate.</p>
      <div className="space-y-3" data-testid="verify-inputs">
        {[3, 7, 11].map((n) => (
          <div key={`verify-${n}`} className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-muted w-20">Parola #{n + 1}</span>
            <input
              type="text"
              value={values[n]}
              onChange={(e) => setValues({ ...values, [n]: e.target.value })}
              className="flex-1 border border-edge rounded-md px-3 py-2 text-sm outline-none focus:border-ink font-mono"
              placeholder="…"
              data-testid={`verify-word-${n}`}
            />
          </div>
        ))}
      </div>
      <button className="big-cta mt-5" disabled={!verifyOk} onClick={onNext} data-testid="verify-next-btn">
        {verifyOk ? "Verifica corretta — continua" : "Compila tutte le 3 parole"}
      </button>
    </div>
  );
}

function StepDone({ name, avatar, onEnter }) {
  return (
    <div className="text-center">
      <div className="inline-flex w-16 h-16 rounded-full bg-primary/10 items-center justify-center mb-3">
        <Check size={28} className="text-primary" />
      </div>
      <h2 className="font-display font-semibold text-3xl mb-1">Profilo creato</h2>
      <p className="text-sm text-muted mb-5">Benvenuto/a, {name || "amico/a"}.</p>
      <div className="inline-flex items-center gap-2 border border-edge rounded-full px-3 py-1.5">
        <img src={avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
        <span className="text-sm font-semibold">{name || "Pseudonimo"}</span>
      </div>
      <button className="big-cta mt-6" onClick={onEnter} data-testid="enter-app-btn">
        Entra nella Home
      </button>
    </div>
  );
}

// ============ Profilo ============
export function ProfileView({
  user,
  profileIdentityId,
  onBack,
  onOpenMessages,
  onLogout,
  onOpenNetwork,
  onOpenNotification,}) {
  const viewedIdentityId =
    profileIdentityId || user?.identityId || null;

  const isOwner =
    !!user?.identityId &&
    viewedIdentityId === user.identityId;

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [profileContentTab, setProfileContentTab] = useState("post");
  const [storyComposerOpen, setStoryComposerOpen] = useState(false);
  const [storyType, setStoryType] = useState("text");
  const [storyText, setStoryText] = useState("");
  const [storyCaption, setStoryCaption] = useState("");
  const [storyHighlighted, setStoryHighlighted] = useState(false);
  const [storyFile, setStoryFile] = useState(null);
  const [storyPreviewUrl, setStoryPreviewUrl] = useState("");
  const [storyMessage, setStoryMessage] = useState("");
  const [profileStories, setProfileStories] = useState([]);
  const [profileStoriesLoading, setProfileStoriesLoading] = useState(false);
  const [profileStoriesError, setProfileStoriesError] = useState("");
  const [storyDeleteBusyId, setStoryDeleteBusyId] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedStoryGroup, setSelectedStoryGroup] = useState([]);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);

  const [paymentCheckoutBusy, setPaymentCheckoutBusy] = useState("");
  const [paymentCheckoutMessage, setPaymentCheckoutMessage] = useState("");
  const [adsCheckoutConfirm, setAdsCheckoutConfirm] = useState(null);

  const adsCheckoutPackages = {
    "ads_start": {
      "name": "ADS Start",
      "price": "4.90",
      "days": "3",
      "coverage": "Base"
    },
    "ads_plus": {
      "name": "ADS Plus",
      "price": "8.90",
      "days": "7",
      "coverage": "Standard"
    },
    "ads_boost": {
      "name": "ADS Boost",
      "price": "14.90",
      "days": "14",
      "coverage": "Ampliata"
    }
  };
  const [paymentEntitlement, setPaymentEntitlement] = useState(null);
  const [paymentEntitlementLoading, setPaymentEntitlementLoading] = useState(false);

  const [paidVideoEnabled, setPaidVideoEnabled] = useState(false);
  const [paidVideoPreviewEnabled, setPaidVideoPreviewEnabled] = useState(false);
  const [paidVideoFile, setPaidVideoFile] = useState(null);
  const [paidVideoPrice, setPaidVideoPrice] = useState("1,99");

  const [
    paidVideoCaption,
    setPaidVideoCaption
  ] = useState("");

  const [
    paidVideoDeleteBusyId,
    setPaidVideoDeleteBusyId
  ] = useState(null);

  // YOUVOICE_COLLAPSIBLE_PROFILE_PANELS_V6
  const [networkPanelOpen, setNetworkPanelOpen] = useState(false);
  const [paidServicesPanelOpen, setPaidServicesPanelOpen] = useState(false);

  // YOUVOICE_PAID_SERVICES_DETAILS_V1
  const [paidServiceDetailsOpen, setPaidServiceDetailsOpen] =
    useState(null);

  // YOUVOICE_EXISTING_PREMIUM_SHEETS_V1
  // "Scopri dettagli" apre le schede informative storiche adattate alla live.
  const [paidVideoPanelOpen, setPaidVideoPanelOpen] = useState(false);

  // YOUVOICE_PAID_VIDEO_PUBLISH_V1
  const [
    paidVideoPublishBusy,
    setPaidVideoPublishBusy
  ] = useState(false);

  const [
    paidVideoPublishMessage,
    setPaidVideoPublishMessage
  ] = useState("");

  
  // YOUVOICE_PAID_VIDEO_LIST_V1
  const [ownPaidVideos, setOwnPaidVideos] = useState([]);
  const [ownPaidVideosLoading, setOwnPaidVideosLoading] =
    useState(false);
  const [ownPaidVideosError, setOwnPaidVideosError] =
    useState("");

  const refreshOwnPaidVideos = async () => {
    if (!isOwner) return [];

    setOwnPaidVideosLoading(true);
    setOwnPaidVideosError("");

    try {
      const items = await fetchOwnPaidVideos();
      const normalized = Array.isArray(items) ? items : [];

      setOwnPaidVideos(normalized);
      return normalized;
    } catch (error) {
      setOwnPaidVideosError(
        error?.response?.data?.message ||
        error?.message ||
        "Impossibile caricare i video a pagamento."
      );
      return [];
    } finally {
      setOwnPaidVideosLoading(false);
    }
  };

  useEffect(() => {
    if (
      !isOwner ||
      profileContentTab !== "video"
    ) {
      return;
    }

    refreshOwnPaidVideos();
  }, [isOwner, profileContentTab]);

  const deletePaidVideo = async (paidVideo) => {
    if (
      !paidVideo?.id ||
      paidVideoDeleteBusyId
    ) {
      return;
    }

    const confirmed = window.confirm(
      "Vuoi eliminare definitivamente questo video a pagamento?"
    );

    if (!confirmed) return;

    setPaidVideoDeleteBusyId(paidVideo.id);
    setOwnPaidVideosError("");

    try {
      await deletePaidVideoRecord(
        paidVideo.id
      );

      if (
        selectedProfileContent?.objectKey ===
        paidVideo.objectKey
      ) {
        setSelectedProfileContent(null);
      }

      await refreshOwnPaidVideos();

    } catch (error) {
      setOwnPaidVideosError(
        error?.response?.data?.message ||
        error?.message ||
        "Eliminazione del video non riuscita."
      );
    } finally {
      setPaidVideoDeleteBusyId(null);
    }
  };

  const publishPaidVideo = async () => {
    if (paidVideoPublishBusy) return;

    setPaidVideoPublishMessage("");

    if (!paidVideoEnabled) {
      setPaidVideoPublishMessage(
        "Attiva prima la vendita dei video."
      );
      return;
    }

    if (!paidVideoFile) {
      setPaidVideoPublishMessage(
        "Seleziona prima un video."
      );
      return;
    }

    if (
      paidVideoFile.type &&
      !paidVideoFile.type.startsWith("video/")
    ) {
      setPaidVideoPublishMessage(
        "Il file selezionato non è un video valido."
      );
      return;
    }

    const parsedPrice = Number(
      String(paidVideoPrice || "")
        .trim()
        .replace(",", ".")
    );

    if (
      !Number.isFinite(parsedPrice) ||
      parsedPrice <= 0
    ) {
      setPaidVideoPublishMessage(
        "Inserisci un prezzo valido."
      );
      return;
    }

    const priceCents = Math.round(
      (parsedPrice + Number.EPSILON) * 100
    );

    if (priceCents < 1) {
      setPaidVideoPublishMessage(
        "Inserisci un prezzo valido."
      );
      return;
    }

    setPaidVideoPublishBusy(true);

    let recordCreated = false;

    try {
      setPaidVideoPublishMessage(
        "Caricamento video..."
      );

      const attachment =
        await uploadPostMedia(paidVideoFile);

      if (!attachment?.objectKey) {
        throw new Error(
          "Upload video non completato."
        );
      }

      /*
       * Il record viene creato PRIMA della moderazione.
       * In questo modo il gateway può aggiornare
       * paid_videos tramite lo stesso objectKey.
       */
      setPaidVideoPublishMessage(
        "Registrazione video a pagamento..."
      );

      const record =
        await createPaidVideoRecord({
          objectKey: attachment.objectKey,
          priceCents,
          previewEnabled:
            Boolean(paidVideoPreviewEnabled),
          caption: paidVideoCaption.trim() || null,
        });

      if (!record?.id) {
        throw new Error(
          "Registrazione del video non riuscita."
        );
      }

      recordCreated = true;

      setPaidVideoPublishMessage(
        "Moderazione video in corso..."
      );

      let moderation = {
        status: "pending",
      };

      /*
       * Come per gli altri video, facciamo
       * alcuni tentativi se il server risponde pending.
       */
      for (let attempt = 0; attempt < 3; attempt += 1) {
        moderation =
          await requestMediaModeration(
            attachment.objectKey,
            "video"
          );

        if (
          moderation?.status &&
          moderation.status !== "pending"
        ) {
          break;
        }

        if (attempt < 2) {
          await new Promise((resolve) =>
            window.setTimeout(
              resolve,
              attempt === 0 ? 4000 : 12000
            )
          );
        }
      }

      const status =
        moderation?.status || "pending";

      if (status === "approved") {
        setPaidVideoPublishMessage(
          "Video approvato e pubblicato."
        );
      } else if (
        status === "rejected" ||
        status === "failed"
      ) {
        setPaidVideoPublishMessage(
          "Video non approvato dalla moderazione."
        );
      } else {
        setPaidVideoPublishMessage(
          "Video registrato. Moderazione ancora in attesa."
        );
      }

      await refreshOwnPaidVideos();

      setPaidVideoFile(null);
      setPaidVideoPreviewEnabled(false);
      setPaidVideoPrice("1,99");
      setPaidVideoCaption("");

    } catch (error) {
      if (recordCreated) {
        setPaidVideoPublishMessage(
          "Video registrato. La moderazione potrà essere completata successivamente."
        );
      } else {
        setPaidVideoPublishMessage(
          error?.response?.data?.message ||
          error?.message ||
          "Pubblicazione del video non riuscita."
        );
      }
    } finally {
      setPaidVideoPublishBusy(false);
    }
  };

useEffect(() => {
    const resetPaymentCheckoutAfterHistory = () => {
      setPaymentCheckoutBusy("");
    };

    window.addEventListener(
      "pageshow",
      resetPaymentCheckoutAfterHistory
    );

    return () => {
      window.removeEventListener(
        "pageshow",
        resetPaymentCheckoutAfterHistory
      );
    };
  }, []);

  useEffect(() => {
    if (!isOwner || !user?.identityId) {
      setPaymentEntitlement(null);
      return;
    }

    let cancelled = false;

    const loadPaymentEntitlement = async () => {
      setPaymentEntitlementLoading(true);

      try {
        const response = await fetch(
          "/api/v1/payments/entitlement",
          {
            method: "GET",
            credentials: "include",
            headers: {
              "x-ol-identity": user.identityId,
            },
          }
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.message ||
            payload?.error ||
            "Impossibile leggere lo stato Premium."
          );
        }

        if (!cancelled) {
          setPaymentEntitlement(payload);
        }
      } catch (error) {
        console.error("Entitlement Premium:", error);

        if (!cancelled) {
          setPaymentEntitlement(null);
        }
      } finally {
        if (!cancelled) {
          setPaymentEntitlementLoading(false);
        }
      }
    };

    loadPaymentEntitlement();

    return () => {
      cancelled = true;
    };
  }, [isOwner, user?.identityId]);

  const premiumPlanRank = {
    premium_start: 1,
    premium_plus: 2,
    creator_pro: 3,
  };

  const activePremiumPlan =
    paymentEntitlement?.active
      ? paymentEntitlement?.plan
      : null;

  const activePremiumRank =
    premiumPlanRank[activePremiumPlan] || 0;

  const premiumStartActive =
    activePremiumPlan === "premium_start";

  const premiumPlusActive =
    activePremiumPlan === "premium_plus";

  const creatorProActive =
    activePremiumPlan === "creator_pro";

  // Diritti Premium cumulativi.
  // Manteniamo premiumStartActive / premiumPlusActive / creatorProActive
  // come indicatori del piano ESATTO acquistato.
  const hasPremiumStart = activePremiumRank >= 1;
  const hasPremiumPlus = activePremiumRank >= 2;
  const hasCreatorPro = activePremiumRank >= 3;

  const premiumFeatures = {
    // Premium Start e superiori
    noAds: hasPremiumStart,
    premiumBaseFeatures: hasPremiumStart,

    // Premium Plus e superiori
    customHumanAvatar: hasPremiumPlus,
    premiumProfileFrame: hasPremiumPlus,
    extraVoiceEffects: hasPremiumPlus,
    privateChatVoiceEffects: hasPremiumPlus,
    higherStorage: hasPremiumPlus,
    largerUploads: hasPremiumPlus,

    // Creator Pro
    creatorTools: hasCreatorPro,
    advancedCreatorAnalytics: hasCreatorPro,
    professionalVoiceChanger: hasCreatorPro,
    highestStorage: hasCreatorPro,
    heavierVideoUploads: hasCreatorPro,
    paidContent: hasCreatorPro,
  };


  const premiumStartIncluded =
    activePremiumRank > premiumPlanRank.premium_start;

  const premiumPlusIncluded =
    activePremiumRank > premiumPlanRank.premium_plus;

  const startPaidCheckout = async (kind, value) => {
    if (!isOwner || !user?.identityId || paymentCheckoutBusy) return;

    const isAds = kind === "ads";

    const endpoint = isAds
      ? "/api/v1/payments/checkout/ads"
      : "/api/v1/payments/checkout/subscription";

    const isProfilePreview =
      window.location.pathname.startsWith("/profile-preview");

    const checkoutBody = isAds
      ? {
          adsPackage: value,
          ...(isProfilePreview
            ? { returnPath: "/profile-preview/payment-return.html" }
            : {}),
        }
      : {
          plan: value,
          ...(isProfilePreview
            ? { returnPath: "/profile-preview/payment-return.html" }
            : {}),
        };

    const busyKey = `${kind}-${value}`;

    setPaymentCheckoutBusy(busyKey);
    setPaymentCheckoutMessage("");

    const checkoutWindow = isProfilePreview
      ? window.open("about:blank", "_blank")
      : null;

    // YOUVOICE_PREMIUM_CONFIRM_PREVIEW
    // Solo per upgrade di un Premium gia attivo.
    // ADS e nuovi abbonamenti continuano col flusso originale.
    if (!isAds && activePremiumRank > 0) {
      const targetPremiumRank =
        premiumPlanRank[value] || 0;

      if (targetPremiumRank > activePremiumRank) {
        try {
          const previewResponse =
            await signedPostPaymentPreview(
              "/api/v1/payments/preview/subscription",
              { plan: value },
            );

          const preview =
            previewResponse?.data ||
            previewResponse ||
            {};

          if (!preview?.preview) {
            throw new Error(
              "Anteprima Stripe non disponibile"
            );
          }

          const currency =
            String(preview.currency || "EUR")
              .toUpperCase();

          const money = (cents) =>
            new Intl.NumberFormat("it-IT", {
              style: "currency",
              currency,
            }).format(
              Number(cents || 0) / 100
            );

          const planLabel = {
            premium_start: "Premium Start",
            premium_plus: "Premium Plus",
            creator_pro: "Creator Pro",
          };

          const currentName =
            planLabel[preview.currentPlan] ||
            preview.currentPlan ||
            "Premium";

          const targetName =
            planLabel[preview.targetPlan] ||
            preview.targetPlan ||
            "Nuovo piano";

          const message =
            "CONFERMA UPGRADE\n\n" +
            "Piano attuale: " + currentName + "\n" +
            "Nuovo piano: " + targetName + "\n\n" +
            "Credito piano attuale: " +
            money(preview.creditAmount) + "\n" +
            "Da pagare oggi: " +
            money(preview.amountDue) + "\n\n" +
            "Confermi l'upgrade e il pagamento?";

          // YOUVOICE_UPGRADE_MODAL_V2
      const confirmed = await new Promise((resolve) => {
        const oldOverflow = document.body.style.overflow;

        const overlay = document.createElement("div");
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");
        overlay.setAttribute(
          "aria-label",
          "Conferma upgrade Premium"
        );

        Object.assign(overlay.style, {
          position: "fixed",
          inset: "0",
          zIndex: "99999",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          background: "rgba(0,0,0,0.58)",
          backdropFilter: "blur(3px)",
        });

        const modal = document.createElement("div");

        Object.assign(modal.style, {
          width: "100%",
          maxWidth: "540px",
          overflow: "hidden",
          borderRadius: "24px",
          border: "2px solid #202020",
          background: "#fffaf2",
          boxShadow: "0 24px 70px rgba(0,0,0,.30)",
          fontFamily: "inherit",
        });

        const header = document.createElement("div");

        Object.assign(header.style, {
          display: "flex",
          alignItems: "center",
          gap: "13px",
          padding: "20px 22px",
          background: "#ffffff",
          borderBottom: "1px solid #eadfce",
        });

        const logo = document.createElement("img");
        logo.src = "/favicon.png";
        logo.alt = "You Voice";

        Object.assign(logo.style, {
          width: "44px",
          height: "44px",
          borderRadius: "11px",
          objectFit: "cover",
        });

        const heading = document.createElement("div");

        const title = document.createElement("div");
        title.textContent = "Conferma upgrade";

        Object.assign(title.style, {
          fontSize: "21px",
          fontWeight: "800",
          color: "#171717",
        });

        const subtitle = document.createElement("div");
        subtitle.textContent =
          currentName + " → " + targetName;

        Object.assign(subtitle.style, {
          marginTop: "2px",
          fontSize: "14px",
          color: "#6d655c",
        });

        heading.appendChild(title);
        heading.appendChild(subtitle);

        header.appendChild(logo);
        header.appendChild(heading);

        const body = document.createElement("div");

        Object.assign(body.style, {
          padding: "22px",
        });

        const summary = document.createElement("div");

        Object.assign(summary.style, {
          display: "grid",
          gap: "10px",
        });

        const makeRow = (label, value, important = false) => {
          const row = document.createElement("div");

          Object.assign(row.style, {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "18px",
            padding: "12px 14px",
            borderRadius: "14px",
            border: "1px solid #eadfce",
            background: "#ffffff",
          });

          const left = document.createElement("span");
          left.textContent = label;

          Object.assign(left.style, {
            fontSize: "14px",
            color: "#625a52",
          });

          const right = document.createElement("strong");
          right.textContent = value;

          Object.assign(right.style, {
            fontSize: important ? "19px" : "15px",
            color: important ? "#e52d27" : "#171717",
            whiteSpace: "nowrap",
          });

          row.appendChild(left);
          row.appendChild(right);

          return row;
        };

        const recurringPrice = {
          premium_start: "€ 2,99 / mese",
          premium_plus: "€ 4,99 / mese",
          creator_pro: "€ 7,99 / mese",
        };

        summary.appendChild(
          makeRow("Piano attuale", currentName)
        );

        summary.appendChild(
          makeRow("Nuovo piano", targetName)
        );

        summary.appendChild(
          makeRow(
            "Credito residuo",
            money(preview.creditAmount)
          )
        );

        summary.appendChild(
          makeRow(
            "Da pagare oggi",
            money(preview.amountDue),
            true
          )
        );

        summary.appendChild(
          makeRow(
            "Nuovo prezzo",
            recurringPrice[preview.targetPlan] ||
              "Piano mensile"
          )
        );

        const note = document.createElement("div");

        note.textContent =
          "Il nuovo ciclo di abbonamento parte da oggi. " +
          "Il pagamento verrà effettuato con il metodo già associato al tuo abbonamento.";

        Object.assign(note.style, {
          marginTop: "16px",
          padding: "13px 14px",
          borderRadius: "14px",
          background: "#f8eee0",
          color: "#625a52",
          fontSize: "13px",
          lineHeight: "1.45",
        });

        const actions = document.createElement("div");

        Object.assign(actions.style, {
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: "10px",
          padding: "0 22px 22px",
        });

        const cancel = document.createElement("button");
        cancel.type = "button";
        cancel.textContent = "Annulla";

        Object.assign(cancel.style, {
          minHeight: "46px",
          padding: "0 22px",
          borderRadius: "999px",
          border: "2px solid #222222",
          background: "#ffffff",
          color: "#171717",
          fontWeight: "700",
          cursor: "pointer",
        });

        const confirm = document.createElement("button");
        confirm.type = "button";
        confirm.textContent =
          "Conferma upgrade e paga " +
          money(preview.amountDue);

        Object.assign(confirm.style, {
          minHeight: "46px",
          padding: "0 22px",
          borderRadius: "999px",
          border: "2px solid #e52d27",
          background: "#e52d27",
          color: "#ffffff",
          fontWeight: "800",
          cursor: "pointer",
        });

        let finished = false;

        const cleanup = (result) => {
          if (finished) return;
          finished = true;

          document.removeEventListener(
            "keydown",
            onKeyDown
          );

          document.body.style.overflow =
            oldOverflow;

          overlay.remove();
          resolve(result);
        };

        const onKeyDown = (event) => {
          if (event.key === "Escape") {
            cleanup(false);
          }
        };

        cancel.addEventListener(
          "click",
          () => cleanup(false)
        );

        confirm.addEventListener(
          "click",
          () => cleanup(true)
        );

        overlay.addEventListener("click", (event) => {
          if (event.target === overlay) {
            cleanup(false);
          }
        });

        document.addEventListener(
          "keydown",
          onKeyDown
        );

        body.appendChild(summary);
        body.appendChild(note);

        actions.appendChild(cancel);
        actions.appendChild(confirm);

        modal.appendChild(header);
        modal.appendChild(body);
        modal.appendChild(actions);

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        document.body.style.overflow = "hidden";

        cancel.focus();
      });

      if (!confirmed) {
            if (
              checkoutWindow &&
              !checkoutWindow.closed
            ) {
              checkoutWindow.close();
            }

            setPaymentCheckoutBusy("");
            setPaymentCheckoutMessage(
              "Upgrade non effettuato."
            );
            return;
          }

        } catch (error) {
          if (
            checkoutWindow &&
            !checkoutWindow.closed
          ) {
            checkoutWindow.close();
          }

          setPaymentCheckoutBusy("");
          setPaymentCheckoutMessage(
            error?.message ||
              "Impossibile calcolare l'anteprima dell'upgrade."
          );
          return;
        }
      }
    }


    if (checkoutWindow) {
      checkoutWindow.opener = null;
      checkoutWindow.document.title = "You Voice - Pagamento";
      checkoutWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:32px;text-align:center">Apertura pagamento sicuro...</div>';
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-ol-identity": user.identityId,
        },
        body: JSON.stringify(checkoutBody),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.message ||
          payload?.error ||
          "Checkout Stripe non disponibile"
        );
      }

      if (!payload?.url) {
        throw new Error("URL Stripe Checkout mancante.");
      }

      if (isProfilePreview && checkoutWindow) {
        checkoutWindow.location.replace(payload.url);
        setPaymentCheckoutBusy("");
        return;
      }

      window.location.assign(payload.url);
    } catch (error) {
      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.close();
      }

      console.error("Stripe checkout:", error);

      setPaymentCheckoutMessage(
        error?.message ||
        "Impossibile aprire il pagamento Stripe."
      );

      setPaymentCheckoutBusy("");
    }
  };
  const [selectedProfileContent, setSelectedProfileContent] = useState(null);

  const [personalProfileContents, setPersonalProfileContents] = useState([]);
  const [personalContentLoading, setPersonalContentLoading] = useState(false);
  const [personalContentError, setPersonalContentError] = useState("");
  const [profileDraftText, setProfileDraftText] = useState("");
  const [profileMediaFile, setProfileMediaFile] = useState(null);
  // YOUVOICE_PROFILE_MEDIA_CAPTION
  const [profileMediaCaption, setProfileMediaCaption] = useState("");
  const [profileMediaPreviewUrl, setProfileMediaPreviewUrl] = useState("");
  const [profilePublishBusy, setProfilePublishBusy] = useState(false);
  const [profileDeleteBusyId, setProfileDeleteBusyId] = useState(null);
  const [profilePublishMessage, setProfilePublishMessage] = useState("");

  useEffect(() => {
    let active = true;

    async function loadProfileData() {
      if (!viewedIdentityId) {
        if (active) {
          setProfile(null);
          setPosts([]);
          setAnalytics(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        if (isOwner) {
          const [publicProfile, network, authored] = await Promise.all([
            fetchProfile(viewedIdentityId),
            fetchMyNetwork(),
            fetchPostsByAuthor(viewedIdentityId),
          ]);

          if (!active) return;

          setProfile(publicProfile || {
            identityId: user.identityId,
            displayName: user.name,
            avatar: user.avatar,
          });

          setAnalytics(network?.analytics || null);
          setPosts(Array.isArray(authored) ? authored : []);
        } else {
          const [publicProfile, authored] = await Promise.all([
            fetchProfile(viewedIdentityId),
            fetchPostsByAuthor(viewedIdentityId),
          ]);

          if (!active) return;

          setProfile(publicProfile || null);
          setPosts(Array.isArray(authored) ? authored : []);

          setAnalytics({
            posts: Array.isArray(authored) ? authored.length : 0,
          });
        }
      } catch (error) {
        if (!active) return;

        setProfile(null);
        setPosts([]);
        setAnalytics(null);
        setLoadError(
          error?.message ||
          "Dati del profilo non disponibili.",
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadProfileData();

    return () => {
      active = false;
    };
  }, [
    viewedIdentityId,
    isOwner,
    user?.identityId,
    user?.name,
    user?.avatar,
  ]);


  const profileContentTabs = [
    { key: "post", label: "Post" },
    { key: "foto", label: "Immagini" },
    { key: "video", label: "Video" },
    { key: "vocali", label: "Vocali" },
  ];

  const profileAttachments = (post) =>
    Array.isArray(post?.attachments) ? post.attachments : [];

  const profileHasImage = (post) =>
    profileAttachments(post).some(
      (item) => String(item?.kind || "").toLowerCase() === "image"
    );

  const profileHasVideo = (post) =>
    profileAttachments(post).some(
      (item) => String(item?.kind || "").toLowerCase() === "video"
    );

  const profileHasVoice = (post) =>
    Boolean(
      post?.audioObjectKey ||
      post?.audioUrl ||
      post?.audio_object_key ||
      post?.audio_url
    );

  const filteredProfilePosts = posts.filter((post) => {
    if (profileContentTab === "foto") return profileHasImage(post);
    if (profileContentTab === "video") return profileHasVideo(post);
    if (profileContentTab === "vocali") return profileHasVoice(post);
    if (profileContentTab === "post") return !profileHasVoice(post);
    return true;
  });

  const openProfilePost = (post) => {
    const postId = post?.id;
    const topicSlug = post?.topicSlug || post?.topic_slug;

    if (
      !postId ||
      !topicSlug ||
      typeof onOpenNotification !== "function"
    ) {
      return;
    }

    onOpenNotification({
      type: "post",
      postId: String(postId),
      topicSlug: String(topicSlug),
    });
  };

  const handleProfilePostCardClick = (event, post) => {
    const target = event.target;

    // Audio, video, pulsanti e link mantengono
    // il loro comportamento normale.
    if (
      target?.closest?.(
        "audio, video, button, a, input, textarea, select"
      )
    ) {
      return;
    }

    openProfilePost(post);
  };


  const profileContentTitle = isOwner
    ? {
        post: "I miei post",
        foto: "Le mie immagini",
        video: "I miei video",
        vocali: "I miei messaggi vocali",
      }[profileContentTab]
    : {
        post: "Post pubblici",
        foto: "Immagini pubbliche",
        video: "Video pubblici",
        vocali: "Vocali pubblici",
      }[profileContentTab];

  const profileEmptyMessage = isOwner
    ? {
        post: "Non hai ancora pubblicato post.",
        foto: "Non hai ancora pubblicato immagini.",
        video: "Non hai ancora pubblicato video.",
        vocali: "Non hai ancora pubblicato messaggi vocali.",
      }[profileContentTab]
    : {
        post: "Questo utente non ha ancora pubblicato post.",
        foto: "Questo utente non ha ancora pubblicato immagini.",
        video: "Questo utente non ha ancora pubblicato video.",
        vocali: "Questo utente non ha ancora pubblicato messaggi vocali.",
      }[profileContentTab];

  const clearStoryComposer = () => {
    if (storyPreviewUrl) {
      URL.revokeObjectURL(storyPreviewUrl);
    }

    setStoryPreviewUrl("");
    setStoryFile(null);
    setStoryText("");
    setStoryCaption("");
    setStoryHighlighted(false);
    setStoryType("text");
    setStoryMessage("");
  };

  const openStorySequence = (stories) => {
    if (!Array.isArray(stories) || stories.length === 0) return;

    setSelectedStoryGroup(stories);
    setSelectedStoryIndex(0);
    setSelectedStory(stories[0]);
  };

  const chooseStoryType = (type) => {
    setStoryType(type);
    setStoryMessage("");

    if (type === "text") {
      if (storyPreviewUrl) {
        URL.revokeObjectURL(storyPreviewUrl);
      }
      setStoryPreviewUrl("");
      setStoryFile(null);
    }
  };

  const handleStoryFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (
      !file.type.startsWith("image/") &&
      !file.type.startsWith("video/")
    ) {
      setStoryMessage("Formato non supportato.");
      event.target.value = "";
      return;
    }

    if (storyPreviewUrl) {
      URL.revokeObjectURL(storyPreviewUrl);
    }

    const objectUrl = URL.createObjectURL(file);

    setStoryFile(file);
    setStoryPreviewUrl(objectUrl);
    setStoryType(file.type.startsWith("image/") ? "image" : "video");
    setStoryMessage("");
  };

  const publishStoryFromProfile = async () => {
    if (
      storyType === "text" &&
      !storyText.trim()
    ) {
      setStoryMessage("Scrivi un testo per la storia.");
      return;
    }

    if (
      (storyType === "image" || storyType === "video") &&
      !storyFile
    ) {
      setStoryMessage("Seleziona prima un file.");
      return;
    }

    try {
      setStoryMessage("Pubblicazione della storia...");

      if (storyType === "text") {
        await createStory({
          kind: "text",
          textContent: storyText.trim(),
          highlighted: storyHighlighted,
        });
      } else {
        const attachment = await uploadPostMedia(storyFile);

        if (!attachment?.objectKey) {
          throw new Error(
            "Il server non ha restituito il file della storia."
          );
        }

        // Prima registriamo la storia come pending.
        // In questo modo il gateway di moderazione può trovarla
        // tramite objectKey e aggiornare il suo stato.
        await createStory({
          kind: storyType,
          objectKey: attachment.objectKey,
        caption: storyCaption.trim() || null,
        highlighted: storyHighlighted,
        });

        const moderationResult = await requestMediaModeration(
          attachment.objectKey,
          storyType
        );

        if (moderationResult?.status !== "approved") {
          setStoryMessage(
            `Storia caricata. Stato moderazione: ${
              moderationResult?.status || "pending"
            }.`
          );
          return;
        }
      }

      await refreshProfileStories();
      clearStoryComposer();
      setStoryMessage("Storia pubblicata su You Voice.");
    } catch (error) {
      setStoryMessage(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Pubblicazione della storia non riuscita."
      );
    }
  };

  const profileMediaUrlFor = (objectKey) => {
    if (!objectKey) return "";

    const safeKey = String(objectKey)
      .split("/")
      .map((part) => encodeURIComponent(part))
      .join("/");

    return `/api/v1/uploads/audio/${safeKey}`;
  };

  const refreshPersonalProfileContents = async () => {
    const targetIdentityId =
      viewedIdentityId || user?.identityId || null;

    if (!targetIdentityId) {
      setPersonalProfileContents([]);
      return;
    }

    setPersonalContentLoading(true);
    setPersonalContentError("");

    try {
      const items = isOwner
        ? await fetchOwnProfileContents()
        : await fetchProfileContents(targetIdentityId);

      setPersonalProfileContents(
        Array.isArray(items) ? items : []
      );
    } catch (error) {
      devDebug(
        "ProfilePreview: profile contents load failed",
        error
      );

      setPersonalProfileContents([]);
      setPersonalContentError(
        error?.response?.data?.message ||
        error?.message ||
        "Contenuti del profilo non disponibili."
      );
    } finally {
      setPersonalContentLoading(false);
    }
  };

  useEffect(() => {
    void refreshPersonalProfileContents();
  }, [viewedIdentityId, isOwner, user?.identityId]);

  const refreshProfileStories = async () => {
    const targetIdentityId =
      viewedIdentityId ||
      user?.identityId ||
      null;

    if (!targetIdentityId) {
      setProfileStories([]);
      return;
    }

    setProfileStoriesLoading(true);
    setProfileStoriesError("");

    try {
      const items = await fetchStoriesByIdentity(targetIdentityId);
      setProfileStories(Array.isArray(items) ? items : []);
    } catch (error) {
      setProfileStories([]);
      setProfileStoriesError(
        error?.response?.data?.message ||
        error?.message ||
        "Storie non disponibili."
      );
    } finally {
      setProfileStoriesLoading(false);
    }
  };

  useEffect(() => {
    void refreshProfileStories();
  }, [viewedIdentityId, user?.identityId]);

  const handleDeleteStory = async (storyId) => {
    if (!storyId || storyDeleteBusyId) return;

    if (!window.confirm("Vuoi eliminare questa storia?")) return;

    setStoryDeleteBusyId(storyId);

    try {
      await deleteStory(storyId);
      await refreshProfileStories();
    } catch (error) {
      setStoryMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Eliminazione della storia non riuscita."
      );
    } finally {
      setStoryDeleteBusyId(null);
    }
  };

  const clearProfileMediaDraft = () => {
    if (profileMediaPreviewUrl) {
      URL.revokeObjectURL(profileMediaPreviewUrl);
    }

    setProfileMediaPreviewUrl("");
    setProfileMediaFile(null);
    setProfileMediaCaption("");
  };

  const handleProfileMediaSelected = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const wantsImage = profileContentTab === "foto";
    const wantsVideo = profileContentTab === "video";

    if (wantsImage && !file.type.startsWith("image/")) {
      setProfilePublishMessage(
        "Seleziona un file immagine."
      );
      event.target.value = "";
      return;
    }

    if (wantsVideo && !file.type.startsWith("video/")) {
      setProfilePublishMessage(
        "Seleziona un file video."
      );
      event.target.value = "";
      return;
    }

    if (profileMediaPreviewUrl) {
      URL.revokeObjectURL(profileMediaPreviewUrl);
    }

    setProfileMediaFile(file);
    setProfileMediaPreviewUrl(
      URL.createObjectURL(file)
    );
    setProfilePublishMessage("");
  };

  const publishPersonalTextPost = async () => {
    const text = profileDraftText.trim();

    if (!text) {
      setProfilePublishMessage(
        "Scrivi prima il testo del post."
      );
      return;
    }

    setProfilePublishBusy(true);
    setProfilePublishMessage("");

    try {
      await createProfileContent({
        kind: "post",
        textContent: text,
      });

      setProfileDraftText("");
      setProfilePublishMessage(
        "Post salvato nel tuo profilo."
      );

      await refreshPersonalProfileContents();
    } catch (error) {
      setProfilePublishMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Pubblicazione non riuscita."
      );
    } finally {
      setProfilePublishBusy(false);
    }
  };

  const publishPersonalMedia = async () => {
    if (!profileMediaFile) {
      setProfilePublishMessage(
        profileContentTab === "foto"
          ? "Seleziona prima un'immagine."
          : "Seleziona prima un video."
      );
      return;
    }

    const kind =
      profileContentTab === "foto"
        ? "image"
        : "video";

    setProfilePublishBusy(true);
    setProfilePublishMessage("");

    try {
      const attachment = await uploadPostMedia(
        profileMediaFile
      );

      if (!attachment?.objectKey) {
        throw new Error(
          "Il server non ha restituito la chiave del file."
        );
      }

      await createProfileContent({
      kind,
      textContent: profileMediaCaption.trim() || null,
      objectKey: attachment.objectKey,
    });

      /*
       * Avvia la stessa moderazione media già usata
       * dall'app, senza creare post nel feed pubblico.
       */
      const moderationResult = await requestMediaModeration(
        attachment.objectKey,
        kind
      );

      clearProfileMediaDraft();

      setProfilePublishMessage(
        moderationResult?.status === "approved"
          ? (
              kind === "image"
                ? "Immagine approvata e pubblicata nel tuo profilo."
                : "Video approvato e pubblicato nel tuo profilo."
            )
          : (
              kind === "image"
                ? `Immagine caricata. Stato moderazione: ${moderationResult?.status || "pending"}.`
                : `Video caricato. Stato moderazione: ${moderationResult?.status || "pending"}.`
            )
      );

      await refreshPersonalProfileContents();
    } catch (error) {
      setProfilePublishMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Caricamento non riuscito."
      );
    } finally {
      setProfilePublishBusy(false);
    }
  };

  const handleDeleteProfileContent = async (item) => {
    if (!item?.id || profileDeleteBusyId) return;

    const confirmed = window.confirm(
      item.kind === "image"
        ? "Vuoi eliminare definitivamente questa immagine dal tuo profilo?"
        : item.kind === "video"
          ? "Vuoi eliminare definitivamente questo video dal tuo profilo?"
          : "Vuoi eliminare definitivamente questo contenuto dal tuo profilo?"
    );

    if (!confirmed) return;

    setProfileDeleteBusyId(item.id);
    setProfilePublishMessage("");

    try {
      await deleteProfileContent(item.id);
      await refreshPersonalProfileContents();

      setProfilePublishMessage(
        item.kind === "image"
          ? "Immagine eliminata dal profilo."
          : item.kind === "video"
            ? "Video eliminato dal profilo."
            : "Contenuto eliminato dal profilo."
      );
    } catch (error) {
      setProfilePublishMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Eliminazione non riuscita."
      );
    } finally {
      setProfileDeleteBusyId(null);
    }
  };

  const personalContentsForActiveTab =
    personalProfileContents.filter((item) => {
      if (profileContentTab === "post") {
        return item.kind === "post";
      }

      if (profileContentTab === "foto") {
        return item.kind === "image";
      }

      if (profileContentTab === "video") {
        return item.kind === "video";
      }

      if (profileContentTab === "vocali") {
        return item.kind === "voice";
      }

      return false;
    });

  const displayUser = {
    identityId: isOwner
      ? user?.identityId
      : profile?.identityId || viewedIdentityId,

    name: isOwner
      ? user?.name
      : profile?.displayName || "Utente",

    avatar: isOwner
      ? profile?.avatar || user?.avatar || ""
      : profile?.avatar || "",

    bio: profile?.bio || user?.bio || "",
    coverObjectKey: profile?.coverObjectKey || "",
    coverKind: profile?.coverKind || "",
    coverModerationStatus:
      profile?.coverModerationStatus || "",
  };

  return (
    <div
      className="min-h-screen bg-cream"
      data-testid="profile-view"
    >
      <ProfileHeader onBack={onBack} />

      <div
        className="w-full max-w-[1180px] mx-auto p-[18px]"
      >
        <ProfileHeaderCard
          user={displayUser}
          isOwner={isOwner}
          premiumFrame={premiumFeatures.premiumProfileFrame}
          customHumanAvatar={premiumFeatures.customHumanAvatar}
        />
        {/* CHAT PRIVATA LIVE */}
        <div
          className="mt-4 flex items-center"
          data-testid="profile-private-chat-area"
        >
          
        </div>


        {profileIdentityId &&
          user?.identityId &&
          String(profileIdentityId) !== String(user.identityId) && (
          null
        )}


        

        <ProfileNavRow
            onOpenMessages={onOpenMessages}
            onLogout={onLogout}
            isOwner={isOwner}
            profileIdentityId={profileIdentityId}
          />

  
      {isOwner ? (
          <>
            <div className="mb-4 flex justify-start">
              <button
                type="button"
                onClick={() =>
                  setNetworkPanelOpen((value) => !value)
                }
                className="flex items-center justify-start gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              >
                {networkPanelOpen
                  ? "Chiudi la mia rete"
                  : "Apri la mia rete"}
                <ArrowRight size={17} />
              </button>
            </div>

            {networkPanelOpen && (
<section
            className="card mb-5 px-5 py-6 sm:px-6"
            data-testid="profile-network-under-posts"
          >
            <div className="text-center">
              <h3 className="font-display text-2xl font-semibold">
                Rete e attività
              </h3>

              <p className="mx-auto mt-1 max-w-2xl text-sm text-muted">
                Persone, pubblicazioni, reazioni e diffusioni collegate al tuo profilo You Voice.
              </p>
            </div>

            <ProfileStats
          analytics={analytics}
          loading={loading}
        />

            {isOwner && (
              <button
                type="button"
                className="mx-auto mt-5 flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                onClick={onOpenNetwork}
              >
                Vai alla mia rete
                <ArrowRight size={17} />
              </button>
            )}
          </section>
            )}
          </>
        ) : (
          <>
            {/* YOUVOICE_PUBLIC_VISITOR_NETWORK_V1 */}

            <div className="mb-4 flex justify-start">
              <button
                type="button"
                onClick={() =>
                  setNetworkPanelOpen((value) => !value)
                }
                className="flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              >
                {networkPanelOpen
                  ? "Chiudi la mia rete"
                  : "Apri la mia rete"}
                <ArrowRight size={17} />
              </button>
            </div>

            {networkPanelOpen && (
              <section
                className="card mb-5 px-5 py-5 sm:px-6"
                data-testid="profile-public-network-summary"
              >
                <h3 className="mb-4 font-display text-xl font-semibold">
                  La mia rete
                </h3>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-xl border border-edge bg-white p-3 text-center">
                    <div className="text-xl font-semibold text-primary">
                      {loading ? "…" : (analytics?.likesReceived ?? 0)}
                    </div>
                    <div className="mt-1 text-xs text-muted">
                      Mi piace ricevuti
                    </div>
                  </div>

                  <div className="rounded-xl border border-edge bg-white p-3 text-center">
                    <div className="text-xl font-semibold text-primary">
                      {loading ? "…" : (analytics?.followers ?? 0)}
                    </div>
                    <div className="mt-1 text-xs text-muted">
                      Follower
                    </div>
                  </div>

                  <div className="rounded-xl border border-edge bg-white p-3 text-center">
                    <div className="text-xl font-semibold text-primary">
                      {loading ? "…" : (analytics?.posts ?? 0)}
                    </div>
                    <div className="mt-1 text-xs text-muted">
                      Post pubblicati
                    </div>
                  </div>

                  <div className="rounded-xl border border-edge bg-white p-3 text-center">
                    <div className="text-xl font-semibold text-primary">
                      {loading ? "…" : (analytics?.sharesReceived ?? 0)}
                    </div>
                    <div className="mt-1 text-xs text-muted">
                      Diffusioni
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

      <section
        id="profile-stories-section"
        data-testid="profile-stories-section"
        className="card mt-6 p-5"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-display font-semibold text-xl">Storie</h3>
            <p className="mt-1 text-sm text-muted">
              Condividi momenti, immagini, video o testo che restano visibili per un tempo limitato.
            </p>
          </div>

          {isOwner && (
            <button
              type="button"
              className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white"
              onClick={() => setStoryComposerOpen((open) => !open)}
            >
              + Crea storia
            </button>
          )}
        </div>

        <div className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-3">

          </div>

          {!profileStoriesLoading && profileStories.length > 0 && (
            <div
              className="mb-4 flex gap-4 overflow-x-auto pb-2"
              data-testid="profile-story-circles"
            >
              {profileStories.map((story) => {
                const created = new Date(story.createdAt);
                const expires = new Date(story.expiresAt);

                const durationHours =
                  (expires.getTime() - created.getTime()) /
                  (60 * 60 * 1000);

                const isHighlighted = durationHours > 36;

                const now = new Date();
                const today = new Date(
                  now.getFullYear(),
                  now.getMonth(),
                  now.getDate()
                );

                const storyDay = new Date(
                  created.getFullYear(),
                  created.getMonth(),
                  created.getDate()
                );

                const dayDifference = Math.round(
                  (today.getTime() - storyDay.getTime()) /
                  (24 * 60 * 60 * 1000)
                );

                const storyDayLabel =
                  dayDifference === 0
                    ? "Oggi"
                    : dayDifference === 1
                      ? "Ieri"
                      : created.toLocaleDateString("it-IT", {
                          day: "2-digit",
                          month: "2-digit",
                        });

                return (
                  <button
                    key={`story-circle-${story.id}`}
                    type="button"
                    className="relative flex min-w-[82px] flex-col items-center"
                    onClick={() => setSelectedStory(story)}
                    title={
                      isHighlighted
                        ? "Storia in evidenza · 48 ore"
                        : "Storia · 24 ore"
                    }
                  >
                    <div
                      className={
                        "relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-[3px] border-primary bg-white " +
                        (isHighlighted ? "ring-2 ring-primary ring-offset-2" : "")
                      }
                    >
                      {story.kind === "image" && story.objectKey && (
                        <img
                          src={profileMediaUrlFor(story.objectKey)}
                          alt="Storia"
                          className="h-full w-full object-cover"
                        />
                      )}

                      {story.kind === "video" && (
                        <div className="flex h-full w-full items-center justify-center bg-black text-2xl text-white">
                          ▶
                        </div>
                      )}

                      {story.kind === "text" && (
                        <div className="flex h-full w-full items-center justify-center bg-cream px-2 text-center text-lg font-semibold">
                          Aa
                        </div>
                      )}

                      {isHighlighted && (
                        <span className="absolute bottom-3 right-3 z-20 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-white">
                          48h
                        </span>
                      )}
                    </div>

                    <span className="mt-2 text-xs text-muted">
                      {storyDayLabel}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {!profileStoriesLoading && profileStories.length > 0 && (() => {
            const orderedStories = [...profileStories].sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );

            const videoStories = orderedStories.filter(
              (story) => story.kind === "video"
            );

            const postStories = orderedStories.filter(
              (story) => story.kind === "text"
            );

            const latestStory = orderedStories[0];
            const latestVideo = videoStories[0] || null;
            const latestPost = postStories[0] || null;

            const has48Hours = (stories) =>
              stories.some((story) => {
                const created = new Date(story.createdAt);
                const expires = new Date(story.expiresAt);

                return (
                  (expires.getTime() - created.getTime()) /
                    (60 * 60 * 1000) >
                  36
                );
              });

            const circleContent = (story, fallback) => {
              if (!story) {
                return (
                  <div className="flex h-full w-full items-center justify-center bg-cream text-xl font-semibold text-muted">
                    {fallback}
                  </div>
                );
              }

              if (story.kind === "image" && story.objectKey) {
                return (
                  <img
                    src={profileMediaUrlFor(story.objectKey)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                );
              }

              if (story.kind === "video") {
                return (
                  <div className="flex h-full w-full items-center justify-center bg-black text-2xl text-white">
                    ▶
                  </div>
                );
              }

              return (
                <div className="flex h-full w-full items-center justify-center bg-cream text-xl font-semibold">
                  Aa
                </div>
              );
            };

            return (
              <div
                className="mb-4 flex gap-5 overflow-x-auto pb-2"
                data-testid="profile-story-groups-safe"
              >
                <button
                  type="button"
                  className="flex min-w-[90px] flex-col items-center"
                  onClick={() => openStorySequence(orderedStories)}
                >
                  <div className="relative h-20 w-20 overflow-hidden rounded-full border-[3px] border-primary bg-white">
                    {circleContent(latestStory, "Aa")}

                    {has48Hours(orderedStories) && (
                      <span className="absolute bottom-2 left-1/2 z-20 -translate-x-1/2 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-white">
                        48h
                      </span>
                    )}
                  </div>

                  <span className="mt-2 text-xs font-semibold">
                    Storie
                  </span>

                  <span className="text-[10px] text-muted">
                    {orderedStories.length}
                  </span>
                </button>

                <button
                  type="button"
                  disabled={!latestVideo}
                  className="flex min-w-[90px] flex-col items-center disabled:opacity-40"
                  onClick={() => openStorySequence(videoStories)}
                >
                  <div className="relative h-20 w-20 overflow-hidden rounded-full border-[3px] border-primary bg-white">
                    {circleContent(latestVideo, "▶")}

                    {has48Hours(videoStories) && (
                      <span className="absolute bottom-2 left-1/2 z-20 -translate-x-1/2 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-white">
                        48h
                      </span>
                    )}
                  </div>

                  <span className="mt-2 text-xs font-semibold">
                    Video
                  </span>

                  <span className="text-[10px] text-muted">
                    {videoStories.length}
                  </span>
                </button>

                <button
                  type="button"
                  disabled={!latestPost}
                  className="flex min-w-[90px] flex-col items-center disabled:opacity-40"
                  onClick={() => openStorySequence(postStories)}
                >
                  <div className="relative h-20 w-20 overflow-hidden rounded-full border-[3px] border-primary bg-white">
                    {circleContent(latestPost, "Aa")}

                    {has48Hours(postStories) && (
                      <span className="absolute bottom-2 left-1/2 z-20 -translate-x-1/2 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-white">
                        48h
                      </span>
                    )}
                  </div>

                  <span className="mt-2 text-xs font-semibold">
                    Post
                  </span>

                  <span className="text-[10px] text-muted">
                    {postStories.length}
                  </span>
                </button>
              </div>
            );
          })()}

          <div className="flex gap-4 overflow-x-auto pb-2">
            {profileStoriesLoading && (
              <div className="text-sm text-muted">
                Caricamento storie...
              </div>
            )}

            {!profileStoriesLoading &&
              profileStories.map((story) => (
                <div
                  key={story.id}
                  className="min-w-[130px] max-w-[180px]"
                >
                  <div className="overflow-hidden rounded-xl border border-edge bg-white">
                    {story.kind === "image" && story.objectKey && (
                      <img
                        src={profileMediaUrlFor(story.objectKey)}
                        alt="Storia"
                        className="h-40 w-full cursor-pointer object-cover"
                        onClick={() => setSelectedStory(story)}
                      />
                    )}

                    {story.kind === "video" && story.objectKey && (
                      <video
                        src={profileMediaUrlFor(story.objectKey)}
                        controls
                        playsInline
                        className="h-40 w-full cursor-pointer object-cover"
                        onClick={() => setSelectedStory(story)}
                      />
                    )}

                    {story.kind === "text" && (
                      <div className="flex h-40 items-center justify-center p-3 text-center text-sm">
                        {story.textContent}
                      </div>
                    )}
                  </div>

                  <div className="mt-1 text-center text-xs text-muted">
                    {story.caption && (
                  <div className="mt-2 px-1 text-sm text-muted">
                    {story.caption}
                  </div>
                )}

                {story.caption && (
                <div className="mt-2 px-1 text-sm text-muted">
                  {story.caption}
                </div>
              )}

              {new Date(story.createdAt).toLocaleString("it-IT", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>

                  {isOwner && (
                    <button
                      type="button"
                      className="mt-1 w-full text-xs font-semibold text-red-600 disabled:opacity-50"
                      disabled={storyDeleteBusyId === story.id}
                      onClick={() => handleDeleteStory(story.id)}
                    >
                      {storyDeleteBusyId === story.id
                        ? "Eliminazione..."
                        : "Elimina"}
                    </button>
                  )}
                </div>
              ))}

            {!profileStoriesLoading &&
              profileStories.length === 0 && (
                <div className="text-sm text-muted">
                  Nessuna storia attiva.
                </div>
              )}
          </div>

          {profileStoriesError && (
            <div className="mt-2 text-sm text-red-600">
              {profileStoriesError}
            </div>
          )}
        </div>

        {selectedStory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedStory(null)}
        >
          <div
            className="relative max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-4">
              <h3 className="font-display text-xl font-semibold">
                Storia
              </h3>

              <button
                type="button"
                className="rounded-full border border-edge px-4 py-2 text-sm font-semibold"
                onClick={() => setSelectedStory(null)}
              >
                Chiudi
              </button>
            </div>

            {selectedStory.kind === "image" && selectedStory.objectKey && (
              <img
                src={profileMediaUrlFor(selectedStory.objectKey)}
                alt="Storia"
                className="max-h-[70vh] w-full object-contain"
              />
            )}

            {selectedStory.kind === "video" && selectedStory.objectKey && (
              <video
                src={profileMediaUrlFor(selectedStory.objectKey)}
                controls
                autoPlay
                playsInline
                className="max-h-[70vh] w-full object-contain"
              />
            )}

            {selectedStory.kind === "text" && (
              <div className="py-10 text-center text-lg">
                {selectedStory.textContent}
              </div>
            )}

            {selectedStory.caption && (
              <div className="mt-4 text-base">
                {selectedStory.caption}
              </div>
            )}

            <div className="mt-3 text-sm text-muted">
              {new Date(selectedStory.createdAt).toLocaleString("it-IT")}
            </div>
          </div>
        </div>
      )}

      {isOwner && storyComposerOpen && (
          <div className="card mt-5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h4 className="font-display font-semibold text-lg">
                Crea una storia
              </h4>

              <button
                type="button"
                className="rounded-full border border-edge px-4 py-2 text-sm font-semibold"
                onClick={() => {
                  clearStoryComposer();
                  setStoryComposerOpen(false);
                }}
              >
                Chiudi
              </button>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <button
                type="button"
                className={
                "inline-flex min-h-[40px] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-3 py-2 text-center text-sm font-semibold transition hover:opacity-90 " + (
                  storyType === "image"
                    ? "border-white bg-primary text-white"
                    : "border-primary bg-white text-primary"
                )
              }
                onClick={() => chooseStoryType("image")}
              >
                Immagine
              </button>

              <button
                type="button"
                className={
                "inline-flex min-h-[40px] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-3 py-2 text-center text-sm font-semibold transition hover:opacity-90 " + (
                  storyType === "video"
                    ? "border-white bg-primary text-white"
                    : "border-primary bg-white text-primary"
                )
              }
                onClick={() => chooseStoryType("video")}
              >
                Video
              </button>

              <button
                type="button"
                className={
                "inline-flex min-h-[40px] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-3 py-2 text-center text-sm font-semibold transition hover:opacity-90 " + (
                  storyType === "text"
                    ? "border-white bg-primary text-white"
                    : "border-primary bg-white text-primary"
                )
              }
                onClick={() => chooseStoryType("text")}
              >
                Solo testo
              </button>
            </div>

            <div className="mt-4 rounded-xl border border-edge bg-white p-3">
              <div className="text-sm font-semibold">
                Durata della storia
              </div>

              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className={
                "inline-flex min-h-[40px] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-4 py-2 text-center text-sm font-semibold transition hover:opacity-90 " +
                (!storyHighlighted
                  ? "border-white bg-primary text-white"
                  : "border-primary bg-white text-primary")
              }
                  onClick={() => setStoryHighlighted(false)}
                >
                  Normale · 24 ore
                </button>

                <button
                  type="button"
                  className={
                "inline-flex min-h-[40px] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-4 py-2 text-center text-sm font-semibold transition hover:opacity-90 " +
                (storyHighlighted
                  ? "border-white bg-primary text-white"
                  : "border-primary bg-white text-primary")
              }
                  onClick={() => setStoryHighlighted(true)}
                >
                  In evidenza · 48 ore
                </button>
              </div>
            </div>

            {(storyType === "image" || storyType === "video") && (
              <div className="mt-4">
                <input
                  type="file"
                  accept={
                    storyType === "image"
                      ? "image/*"
                      : "video/*"
                  }
                  onChange={handleStoryFileChange}
                  className="block w-full rounded-xl border border-edge bg-white p-3"
                />
              </div>
            )}

            {storyType === "text" && (
              <textarea
                value={storyText}
                onChange={(event) => setStoryText(event.target.value)}
                className="mt-4 min-h-[130px] w-full rounded-xl border border-edge bg-white p-3"
                placeholder="Scrivi un testo per la storia..."
              />
            )}

            {storyPreviewUrl && storyType === "image" && (
              <div className="mt-4 overflow-hidden rounded-xl border border-edge">
                <img
                  src={storyPreviewUrl}
                  alt="Anteprima storia"
                  className="max-h-[420px] w-full object-contain"
                />
              </div>
            )}

            {storyPreviewUrl && storyType === "video" && (
              <div className="mt-4 overflow-hidden rounded-xl border border-edge">
                <video
                  src={storyPreviewUrl}
                  muted
                  loop
                  autoPlay
                  playsInline
                  controls
                  className="max-h-[420px] w-full"
                />
              </div>
            )}

            {storyMessage && (
              <div className="mt-3 text-sm text-muted">
                {storyMessage}
              </div>
            )}

            {(storyType === "image" || storyType === "video") && (
          <div className="mt-4">
            <label
              htmlFor="story-caption"
              className="mb-2 block text-sm font-semibold"
            >
              Didascalia facoltativa
            </label>
            <textarea
              id="story-caption"
              value={storyCaption}
              onChange={(event) => setStoryCaption(event.target.value)}
              rows={3}
              maxLength={500}
              className="w-full rounded-xl border border-edge bg-white p-3 text-sm"
              placeholder="Scrivi una didascalia per questa storia..."
            />
          </div>
        )}

        <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-full border border-edge px-5 py-2 font-semibold"
                onClick={clearStoryComposer}
              >
                Reimposta
              </button>

              <button
                type="button"
                className="rounded-full bg-primary px-5 py-2 font-semibold text-white"
                onClick={publishStoryFromProfile}
              >
                Pubblica
              </button>
            </div>
          </div>
        )}
        )}
      </section>

<h3 className="font-display font-semibold text-2xl mt-6 mb-3">
        {profileContentTitle}
      </h3>

        



      <nav
        id="profile-tabs"
        className="mt-5 flex overflow-x-auto border-b border-edge"
        aria-label="Contenuti del profilo"
      >
        {profileContentTabs.map((tab) => {
          const active = profileContentTab === tab.key;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setProfileContentTab(tab.key)}
              className={
                "whitespace-nowrap px-5 py-3 font-semibold border-b-[3px] " +
                (active
                  ? "text-primary border-primary"
                  : "text-muted border-transparent")
              }
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {isOwner && (
        <div
          className="card mb-5 p-4"
          data-testid="profile-content-composer"
        >
          {profileContentTab === "post" && (
            <>
              <div className="font-semibold">
                Crea un post nel tuo profilo
              </div>

              <textarea
                value={profileDraftText}
                onChange={(event) =>
                  setProfileDraftText(
                    event.target.value
                  )
                }
                className="mt-3 min-h-[120px] w-full rounded-xl border border-edge bg-white p-3"
                placeholder="Scrivi qualcosa da mostrare nel tuo profilo..."
              />

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  className="rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:opacity-50"
                  disabled={profilePublishBusy}
                  onClick={publishPersonalTextPost}
                >
                  {profilePublishBusy
                    ? "Pubblicazione..."
                    : "Pubblica"}
                </button>
              </div>
            </>
          )}

          {profileContentTab === "foto" && (
            <>
              <div className="font-semibold">
                Carica un'immagine nel tuo profilo
              </div>

              <input
                type="file"
                accept="image/*"
                className="mt-3 block w-full rounded-xl border border-edge bg-white p-3"
                onChange={handleProfileMediaSelected}
              />

              {profileMediaPreviewUrl && (
                <div className="mt-4 overflow-hidden rounded-xl border border-edge">
                  <img
                    src={profileMediaPreviewUrl}
                    alt="Anteprima immagine"
                    className="max-h-[480px] w-full object-contain"
                  />
                </div>
              )}

              <div className="mt-3">

                <label className="mb-1 block text-sm font-semibold text-ink">

                  Didascalia

                </label>


                <textarea

                  value={profileMediaCaption}

                  onChange={(event) =>

                    setProfileMediaCaption(event.target.value)

                  }

                  maxLength={500}

                  rows={3}

                  className="w-full rounded-xl border border-edge bg-white px-3 py-2 text-sm text-ink outline-none focus:border-primary"

                  placeholder="Scrivi una didascalia per questa immagine..."

                />


                <div className="mt-1 text-right text-xs text-muted">

                  {profileMediaCaption.length}/500

                </div>

              </div>


              <div className="mt-3 flex justify-end gap-3">
                {profileMediaFile && (
                  <button
                    type="button"
                    className="rounded-full border border-edge px-5 py-2 font-semibold"
                    onClick={clearProfileMediaDraft}
                  >
                    Annulla
                  </button>
                )}

                <button
                  type="button"
                  className="rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:opacity-50"
                  disabled={
                    profilePublishBusy ||
                    !profileMediaFile
                  }
                  onClick={publishPersonalMedia}
                >
                  {profilePublishBusy
                    ? "Caricamento..."
                    : "Pubblica immagine"}
                </button>
              </div>
            </>
          )}

          {profileContentTab === "video" && (
            <>
              <div className="font-semibold">
                Carica un video nel tuo profilo
              </div>

              <input
                type="file"
                accept="video/*"
                className="mt-3 block w-full rounded-xl border border-edge bg-white p-3"
                onChange={handleProfileMediaSelected}
              />

              {profileMediaPreviewUrl && (
                <div className="mt-4 overflow-hidden rounded-xl border border-edge">
                  <video
                    src={profileMediaPreviewUrl}
                    controls
                    playsInline
                    className="max-h-[480px] w-full"
                  />
                </div>
              )}

              <div className="mt-3">

                <label className="mb-1 block text-sm font-semibold text-ink">

                  Didascalia

                </label>


                <textarea

                  value={profileMediaCaption}

                  onChange={(event) =>

                    setProfileMediaCaption(event.target.value)

                  }

                  maxLength={500}

                  rows={3}

                  className="w-full rounded-xl border border-edge bg-white px-3 py-2 text-sm text-ink outline-none focus:border-primary"

                  placeholder="Scrivi una didascalia per questo video..."

                />


                <div className="mt-1 text-right text-xs text-muted">

                  {profileMediaCaption.length}/500

                </div>

              </div>


              <div className="mt-3 flex justify-end gap-3">
                {profileMediaFile && (
                  <button
                    type="button"
                    className="rounded-full border border-edge px-5 py-2 font-semibold"
                    onClick={clearProfileMediaDraft}
                  >
                    Annulla
                  </button>
                )}

                <button
                  type="button"
                  className="rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:opacity-50"
                  disabled={
                    profilePublishBusy ||
                    !profileMediaFile
                  }
                  onClick={publishPersonalMedia}
                >
                  {profilePublishBusy
                    ? "Caricamento..."
                    : "Pubblica video"}
                </button>
              </div>
            </>
          )}

          {profileContentTab === "vocali" && (
            <div className="text-sm text-muted">
              Il caricamento e la registrazione vocale
              verranno collegati al registratore You Voice
              nel passaggio successivo.
            </div>
          )}

          {profilePublishMessage && (
            <div className="mt-3 text-sm text-muted">
              {profilePublishMessage}
            </div>
          )}
        </div>
      )}

      {personalContentLoading && (
        <div className="card mb-4 p-4 text-sm text-muted">
          Caricamento contenuti del profilo...
        </div>
      )}

      {personalContentError && (
        <div className="card mb-4 p-4 text-sm text-primary">
          {personalContentError}
        </div>
      )}

                  {/* YOUVOICE_PAID_VIDEO_RENDER_FIX_V1 */}
            {isOwner && profileContentTab === "video" && (
              <section className="mb-6 rounded-xl border border-edge bg-white p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-ink">
                      I miei video a pagamento
                    </h3>
                    <p className="mt-1 text-xs text-muted">
                      Video Creator Pro caricati, moderati e destinati alla vendita.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={refreshOwnPaidVideos}
                    disabled={ownPaidVideosLoading}
                    className="rounded-full border border-edge bg-white px-4 py-2 text-xs font-semibold text-ink hover:bg-cream disabled:opacity-50"
                  >
                    {ownPaidVideosLoading
                      ? "Aggiornamento..."
                      : "Aggiorna"}
                  </button>
                </div>

                {ownPaidVideosError && (
                  <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {ownPaidVideosError}
                  </div>
                )}

                {ownPaidVideosLoading &&
                ownPaidVideos.length === 0 ? (
                  <div className="py-5 text-center text-sm text-muted">
                    Caricamento video a pagamento...
                  </div>
                ) : ownPaidVideos.length === 0 ? (
                  <div className="py-5 text-center text-sm text-muted">
                    Non hai ancora video a pagamento.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {ownPaidVideos.map((paidVideo) => {
                      const price =
                        Number(paidVideo.priceCents || 0) / 100;

                      const publicationStatus =
                        paidVideo.publicationStatus || "pending";

                      const moderationStatus =
                        paidVideo.moderationStatus || "pending";

                      const statusLabel =
                        publicationStatus === "published"
                          ? "Pubblicato"
                          : publicationStatus === "rejected"
                          ? "Rifiutato"
                          : moderationStatus === "pending"
                          ? "In moderazione"
                          : moderationStatus;

                      return (
                        <article
                          key={paidVideo.id}
                          className="overflow-hidden rounded-xl border border-edge bg-cream/30"
                        >
                          {paidVideo.objectKey && (
                            <video
                              src={profileMediaUrlFor(
                                paidVideo.objectKey
                              )}
                              controls
                              preload="metadata"
                              playsInline
                              title="Apri video"
                              onClick={() =>
                                setSelectedProfileContent({
                                  kind: "video",
                                  objectKey:
                                    paidVideo.objectKey,
                                  caption:
                                    paidVideo.caption || null,
                                })
                              }
                              className="max-h-[360px] w-full cursor-pointer bg-black object-contain"
                            />
                          )}

                          <div className="p-3">
                            <div className="flex items-center justify-between gap-3">
                              <strong className="text-lg text-primary">
                                € {price
                                  .toFixed(2)
                                  .replace(".", ",")}
                              </strong>

                              <span className="rounded-full border border-edge bg-white px-3 py-1 text-xs font-semibold">
                                {statusLabel}
                              </span>
                            </div>

                            {paidVideo.caption && (
                              <div className="mt-3 whitespace-pre-wrap text-sm text-ink">
                                {paidVideo.caption}
                              </div>
                            )}

                            <div className="mt-2 text-xs text-muted">
                              Anteprima gratuita:{" "}
                              {paidVideo.previewEnabled
                                ? "Sì"
                                : "No"}
                            </div>

                            {paidVideo.createdAt && (
                              <div className="mt-1 text-xs text-muted">
                                Caricato:{" "}
                                {new Date(
                                  paidVideo.createdAt
                                ).toLocaleString("it-IT")}
                              </div>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedProfileContent({
                                    kind: "video",
                                    objectKey:
                                      paidVideo.objectKey,
                                    caption:
                                      paidVideo.caption || null,
                                  })
                                }
                                className="rounded-full border border-edge bg-white px-4 py-2 text-xs font-semibold text-ink hover:bg-cream"
                              >
                                Apri
                              </button>

                              <button
                                type="button"
                                disabled={
                                  paidVideoDeleteBusyId ===
                                  paidVideo.id
                                }
                                onClick={() =>
                                  deletePaidVideo(paidVideo)
                                }
                                className="rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                {paidVideoDeleteBusyId ===
                                paidVideo.id
                                  ? "Eliminazione..."
                                  : "Elimina"}
                              </button>
                            </div>

                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* YOUVOICE_MOVE_PAID_VIDEO_FORM_V1 */}
{isOwner && (
        <section
          className="card mb-5 p-4"
          data-testid="profile-paid-video-intro"
        >
          <h3 className="text-lg font-semibold">
            Video a pagamento
          </h3>

          <p className="mt-1 text-sm text-muted">
            La vendita di video è disponibile con il piano Creator Pro.
          </p>

          <button
            type="button"
            className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={!!paymentCheckoutBusy}
            onClick={() =>
                    window.location.assign("/profile-lab/checkout.html?plan=creator-pro")
                  }
          >
            {paymentCheckoutBusy === "subscription-creator_pro"
              ? "Apertura Stripe..."
              : "Scopri Creator Pro"}
          </button>
        </section>
      )}

      {isOwner && (
          <>
        <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={() =>
                setPaidVideoPanelOpen((value) => !value)
              }
              className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            >
              {paidVideoPanelOpen
                ? "Chiudi video a pagamento"
                : "Video a pagamento"}
            </button>
          </div>

          {paidVideoPanelOpen && (
            <section
          className="card mb-5 p-4"
          data-testid="profile-paid-video-settings"
        >
          <div className="mb-4">
            <h3 className="text-lg font-semibold">
              Video a pagamento
            </h3>

            <p className="mt-1 text-sm text-muted">
              Scegli se vendere un tuo video e imposta il prezzo
              che gli utenti dovranno pagare per visualizzarlo.
            </p>
          </div>

          <div className="divide-y divide-edge">
            <label className="flex items-center justify-between gap-4 py-3">
              <span className="text-sm">
                Vendi questo video
              </span>

              <input
                type="checkbox"
                checked={paidVideoEnabled}
                onChange={(event) =>
                  setPaidVideoEnabled(event.target.checked)
                }
                className="h-5 w-5"
              />
            </label>

            <label className="flex items-center justify-between gap-4 py-3">
              <span className="text-sm">
                Anteprima gratuita
              </span>

              <input
                type="checkbox"
                checked={paidVideoPreviewEnabled}
                onChange={(event) =>
                  setPaidVideoPreviewEnabled(event.target.checked)
                }
                className="h-5 w-5"
              />
            </label>
          </div>

          <div className="mt-4 rounded-xl border border-edge bg-white p-4">
            <div className="font-semibold">
              Video da vendere
            </div>

            <input
              type="file"
              accept="video/*"
              className="mt-3 block w-full rounded-xl border border-edge bg-white p-3 text-sm"
              onChange={(event) =>
                setPaidVideoFile(event.target.files?.[0] || null)
              }
            />

            <div className="mt-2 text-xs text-muted">
              {paidVideoFile
                ? paidVideoFile.name
                : "Nessun video selezionato"}
            </div>
          </div>

                      <div className="mt-4">
              <label className="mb-1 block text-sm font-semibold text-ink">
                Didascalia
              </label>

              <textarea
                value={paidVideoCaption}
                onChange={(event) =>
                  setPaidVideoCaption(event.target.value)
                }
                maxLength={1000}
                rows={3}
                placeholder="Scrivi una didascalia per questo video..."
                className="w-full resize-y rounded-xl border border-edge bg-white px-3 py-2 text-sm"
              />

              <div className="mt-1 text-right text-xs text-muted">
                {paidVideoCaption.length}/1000
              </div>
            </div>

<div className="mt-4">
            <div className="font-semibold">
              Link di condivisione
            </div>

            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                readOnly
                value="Disponibile dopo la pubblicazione"
                className="min-w-0 flex-1 rounded-xl border border-edge bg-white px-3 py-2 text-sm"
              />

              <button
                type="button"
                disabled
                className="rounded-full border border-edge px-4 py-2 text-sm font-semibold opacity-50"
              >
                Copia link
              </button>

              <button
                type="button"
                disabled
                className="rounded-full border border-edge px-4 py-2 text-sm font-semibold opacity-50"
              >
                Condividi
              </button>
            </div>
          </div>

          <div className="mt-4">
            <label className="font-semibold">
              Prezzo di vendita
            </label>

            <div className="mt-2 flex items-center gap-2">
              <span className="text-lg font-bold">€</span>

              <input
                type="text"
                inputMode="decimal"
                value={paidVideoPrice}
                onChange={(event) =>
                  setPaidVideoPrice(event.target.value)
                }
                className="w-32 rounded-xl border border-edge bg-white px-3 py-2"
              />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-edge bg-white p-4">
            <div className="flex items-center justify-between gap-4">
              <span className="font-semibold">
                Ricavo stimato creator
              </span>

              <strong className="text-lg text-primary">
                €{" "}
                {(
                  Math.max(
                    0,
                    Number(
                      String(paidVideoPrice)
                        .replace(",", ".")
                    ) || 0
                  ) * 0.9
                )
                  .toFixed(2)
                  .replace(".", ",")}
              </strong>
            </div>

            <p className="mt-2 text-xs text-muted">
              Calcolo indicativo con commissione del 10%.
              Commissioni, imposte e condizioni definitive
              saranno applicate quando il servizio di vendita
              video verrà attivato.
            </p>
          </div>

          <button
              type="button"
              onClick={publishPaidVideo}
              disabled={
                paidVideoPublishBusy ||
                !paidVideoEnabled ||
                !paidVideoFile
              }
              className="mt-4 w-full rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {paidVideoPublishBusy
                ? "Pubblicazione in corso..."
                : "Pubblica video a pagamento"}
            </button>

            {paidVideoPublishMessage && (
              <div className="mt-3 rounded-xl border border-edge bg-white px-3 py-2 text-sm text-ink">
                {paidVideoPublishMessage}
              </div>
            )}
        </section>
          )}
                </>
        )}


            

            {personalContentsForActiveTab.length > 0 && (
        <div
          className={
            profileContentTab === "foto" ||
            profileContentTab === "video"
              ? "mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2"
              : "mb-6 space-y-3"
          }
        >
          {personalContentsForActiveTab.map(
            (item) => (
              <div
                key={item.id}
                className="card overflow-hidden p-4"
              >
                {item.kind === "post" && (
                  <div className="whitespace-pre-wrap text-sm">
                    {item.textContent}
                  </div>
                )}

                {item.kind === "image" &&
                  item.objectKey && (
                    <img
                      src={profileMediaUrlFor(
                        item.objectKey
                      )}
                      alt="Contenuto del profilo"
                      className="max-h-[480px] w-full cursor-pointer rounded-xl object-contain"
                      onClick={() => setSelectedProfileContent(item)}
                    />
                  )}

                {item.kind === "video" &&
                  item.objectKey && (
                    <video
                      src={profileMediaUrlFor(
                        item.objectKey
                      )}
                      controls
                      playsInline
                      className="max-h-[480px] w-full cursor-pointer rounded-xl"
                      onClick={() => setSelectedProfileContent(item)}
                    />
                  )}

                {isOwner && (
                  <>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="text-[11px] text-muted">
                        Stato:{" "}
                        {item.moderationStatus ||
                          "pending"}
                      </div>

                      <button
                        type="button"
                        className="rounded-full border border-red-500 px-3 py-1 text-xs font-semibold text-red-600 disabled:opacity-50"
                        disabled={profileDeleteBusyId === item.id}
                        onClick={() => handleDeleteProfileContent(item)}
                      >
                        {profileDeleteBusyId === item.id
                          ? "Eliminazione..."
                          : "Elimina"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )
          )}
        </div>
      )}

      {selectedProfileContent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedProfileContent(null)}
        >
          <div
            className="relative max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-4">
              <h3 className="font-display text-xl font-semibold">
                Contenuto del profilo
              </h3>

              <button
                type="button"
                className="rounded-full border border-edge px-4 py-2 text-sm font-semibold"
                onClick={() => setSelectedProfileContent(null)}
              >
                Chiudi
              </button>
            </div>

            {selectedProfileContent.kind === "image" &&
              selectedProfileContent.objectKey && (
                <img
                  src={profileMediaUrlFor(selectedProfileContent.objectKey)}
                  alt="Contenuto del profilo"
                  className="max-h-[70vh] w-full object-contain"
                />
              )}

            {selectedProfileContent.kind === "video" &&
              selectedProfileContent.objectKey && (
                <video
                  src={profileMediaUrlFor(selectedProfileContent.objectKey)}
                  controls
                  autoPlay
                  playsInline
                  className="max-h-[70vh] w-full object-contain"
                />
              )}

            {selectedProfileContent.textContent && (
              <div className="mt-4 whitespace-pre-wrap text-base">
                {selectedProfileContent.textContent}
              </div>
            )}

            {selectedProfileContent.caption && (
              <div className="mt-3 text-base text-muted">
                {selectedProfileContent.caption}
              </div>
            )}

            {selectedProfileContent.createdAt && (
              <div className="mt-3 text-sm text-muted">
                {new Date(selectedProfileContent.createdAt).toLocaleString("it-IT")}
              </div>
            )}
          </div>
        </div>
      )}

      {loadError && (
          <div className="card p-4 text-sm text-primary">
            {loadError}
          </div>
        )}

        {!loadError && loading && (
          <div className="card p-4 text-sm text-muted">
            Caricamento...
          </div>
        )}

        {!loadError &&
          !loading &&
          filteredProfilePosts.length === 0 && (
            <div
              className="card p-6 text-center text-sm text-muted"
              data-testid="profile-posts-empty"
            >
              {profileEmptyMessage}
            </div>
          )}

        <div className="space-y-2">
          {filteredProfilePosts.map((post) => (
            <div
              key={post.id}
              className="card p-3 flex items-center gap-3 cursor-pointer transition hover:bg-cream"
              data-testid={`my-track-${post.id}`}
                              role="button"
                  tabIndex={0}
                  onClick={(event) =>
                    handleProfilePostCardClick(event, post)
                  }
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;

                    if (
                      event.key === "Enter" ||
                      event.key === " "
                    ) {
                      event.preventDefault();
                      openProfilePost(post);
                    }
                  }}
                >
              <div className="mic-btn" aria-hidden="true">
                <Radio size={16} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">
                  {post.topicSlug}
                </div>

                <div className="text-xs text-muted">
                  {new Date(post.publishedAt).toLocaleString("it-IT")}
                  {" · "}
                  {Math.floor((post.durationSeconds || 0) / 60)}
                  :
                  {String((post.durationSeconds || 0) % 60).padStart(2, "0")}
                </div>

                {profileContentTab === "vocali" && (
                  <audio
                    controls
                    preload="none"
                    className="mt-2 w-full"
                    src={
                      post.audioUrl ||
                      post.audio_url ||
                      ((post.audioObjectKey || post.audio_object_key)
                        ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/uploads/audio/${post.audioObjectKey || post.audio_object_key}`
                        : undefined)
                    }
                  />
                )}
              </div>
            </div>
          ))}
        </div>

      {isOwner && (
        <>
          <div className="mt-6 mb-6 flex justify-end">
            <button
              type="button"
              onClick={() =>
                setPaidServicesPanelOpen((value) => !value)
              }
              className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            >
              {paidServicesPanelOpen
                ? "Chiudi servizi a pagamento"
                : "Servizi a pagamento"}
            </button>
          </div>

          {paidServicesPanelOpen && (
<section
          className="card mb-5 p-4"
          data-testid="profile-paid-services"
        >
          <div className="mb-4">
            <h3 className="text-lg font-semibold">
              Servizi a pagamento
            </h3>

            <p className="mt-1 text-sm text-muted">
              Gestisci pubblicità e servizi Premium del tuo profilo.
              Questa sezione è visibile solo a te.
            </p>
          </div>

          <div className="mb-6">
            <h4 className="mb-3 font-semibold">
              Pubblicità
            </h4>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-edge bg-white p-4">
                <div className="font-semibold">START</div>
                <div className="mt-1 text-2xl font-bold text-primary">
                  € 4,90
                </div>
                <div className="mt-1 text-sm">3 giorni</div>
                <div className="mt-2 text-xs text-muted">
                  Copertura base
                  <br />
                  1.000–2.000 visualizzazioni indicative
                </div>

                <button
                  type="button"
                  className="mt-4 w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={!!paymentCheckoutBusy}
                  onClick={() => {
                  if (window.location.pathname.startsWith("/profile-preview")) {
                    setPaymentCheckoutMessage("");
                    setAdsCheckoutConfirm("ads_start");
                  } else {
                    startPaidCheckout("ads", "ads_start");
                  }
                }}
                >
                  {paymentCheckoutBusy === "ads-ads_start"
                    ? "Apertura Stripe..."
                    : "Scegli START"}
                </button>
              </div>

              <div className="rounded-xl border border-edge bg-white p-4">
                <div className="font-semibold">PLUS</div>
                <div className="mt-1 text-2xl font-bold text-primary">
                  € 8,90
                </div>
                <div className="mt-1 text-sm">7 giorni</div>
                <div className="mt-2 text-xs text-muted">
                  Copertura standard
                  <br />
                  2.000–4.000 visualizzazioni indicative
                </div>

                <button
                  type="button"
                  className="mt-4 w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={!!paymentCheckoutBusy}
                  onClick={() => {
                  if (window.location.pathname.startsWith("/profile-preview")) {
                    setPaymentCheckoutMessage("");
                    setAdsCheckoutConfirm("ads_plus");
                  } else {
                    startPaidCheckout("ads", "ads_plus");
                  }
                }}
                >
                  {paymentCheckoutBusy === "ads-ads_plus"
                    ? "Apertura Stripe..."
                    : "Scegli PLUS"}
                </button>
              </div>

              <div className="rounded-xl border border-edge bg-white p-4">
                <div className="font-semibold">BOOST</div>
                <div className="mt-1 text-2xl font-bold text-primary">
                  € 14,90
                </div>
                <div className="mt-1 text-sm">14 giorni</div>
                <div className="mt-2 text-xs text-muted">
                  Copertura ampliata
                  <br />
                  4.000–8.000 visualizzazioni indicative
                </div>

                <button
                  type="button"
                  className="mt-4 w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={!!paymentCheckoutBusy}
                  onClick={() => {
                  if (window.location.pathname.startsWith("/profile-preview")) {
                    setPaymentCheckoutMessage("");
                    setAdsCheckoutConfirm("ads_boost");
                  } else {
                    startPaidCheckout("ads", "ads_boost");
                  }
                }}
                >
                  {paymentCheckoutBusy === "ads-ads_boost"
                    ? "Apertura Stripe..."
                    : "Scegli BOOST"}
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h4 className="font-semibold">
                Premium
              </h4>

              {paymentEntitlementLoading ? (
                <span className="text-xs text-muted">
                  Verifica piano...
                </span>
              ) : paymentEntitlement?.active ? (
                <span
                  className="rounded-full px-3 py-1 text-xs font-semibold"
                  style={{
                    backgroundColor: "#4ade80",
                    color: "#000000",
                  }}
                >
                  Piano attivo: {
                    paymentEntitlement.plan === "premium_start"
                      ? "Premium Start"
                      : paymentEntitlement.plan === "premium_plus"
                        ? "Premium Plus"
                        : paymentEntitlement.plan === "creator_pro"
                          ? "Creator Pro"
                          : paymentEntitlement.plan
                  }
                </span>
              ) : null}
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-edge bg-white p-4 flex h-full flex-col">
                <div className="font-semibold">Premium Start</div>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="rounded-full border border-edge px-4 py-2 text-sm font-semibold hover:bg-cream"
                      onClick={() =>
                        window.location.assign(
                          "/checkout.html?plan=premium-start"
                        )
                      }
                    >
                      Scopri dettagli
                    </button>
                  </div>

                  {paidServiceDetailsOpen === "premium_start" && (
                    <div className="mt-3 rounded-xl border border-edge bg-cream/60 p-4 text-sm text-muted">
                      <div className="font-semibold text-ink">
                        Cosa include Premium Start
                      </div>
                      <p className="mt-2">
                        È il primo livello Premium di You Voice.
                        Rimuove la pubblicità e abilita le funzioni
                        Premium di base previste dal piano.
                      </p>
                      <p className="mt-2">
                        Le funzioni dei livelli superiori restano
                        separate e possono essere attivate passando
                        a Premium Plus o Creator Pro.
                      </p>
                    </div>
                  )}
                <div className="mt-1 text-2xl font-bold text-primary">
                  € 2,99
                  <span className="text-sm font-normal text-muted">
                    {" "}/ mese
                  </span>
                </div>

                <div className="mt-2 text-xs text-muted">
                  Eliminazione della pubblicità
                </div>

                <button
                  type="button"
                  className="mt-auto w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={
                    !!paymentCheckoutBusy ||
                    paymentEntitlementLoading ||
                    premiumStartActive ||
                    premiumStartIncluded
                  }
                  onClick={() =>
                    (window.location.pathname.startsWith("/profile-preview") ? window.location.assign("/profile-preview/checkout.html?plan=premium-start") : startPaidCheckout("subscription", "premium_start"))
                  }
                >
                  {paymentCheckoutBusy === "subscription-premium_start"
                    ? "Apertura Stripe..."
                    : premiumStartActive
                      ? "Premium Start attivo"
                      : premiumStartIncluded
                        ? "Incluso nel tuo piano"
                        : "Attiva Premium Start"}
                </button>
              </div>

              <div className="rounded-xl border border-edge bg-white p-4 flex h-full flex-col">
                <div className="font-semibold">Premium Plus</div>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="rounded-full border border-edge px-4 py-2 text-sm font-semibold hover:bg-cream"
                      onClick={() =>
                        window.location.assign(
                          "/checkout.html?plan=premium-plus"
                        )
                      }
                    >
                      Scopri dettagli
                    </button>
                  </div>

                  {paidServiceDetailsOpen === "premium_plus" && (
                    <div className="mt-3 rounded-xl border border-edge bg-cream/60 p-4 text-sm text-muted">
                      <div className="font-semibold text-ink">
                        Cosa include Premium Plus
                      </div>
                      <p className="mt-2">
                        Comprende le funzioni di Premium Start e
                        aggiunge le funzioni avanzate del profilo.
                      </p>
                      <p className="mt-2">
                        Include avatar umano personalizzabile,
                        cornice Premium del profilo, effetti vocali
                        aggiuntivi, effetti voce avanzati anche nelle chat private,
                        maggiore spazio disponibile e limiti di
                        caricamento superiori.
                      </p>
                    </div>
                  )}
                <div className="mt-1 text-2xl font-bold text-primary">
                  € 4,99
                  <span className="text-sm font-normal text-muted">
                    {" "}/ mese
                  </span>
                </div>

                <div className="mt-2 text-xs text-muted">
                  Tutte le funzioni di Premium Start
                  <br />
                  Cornice Premium
                </div>

                <button
                  type="button"
                  className="mt-auto w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={
                    !!paymentCheckoutBusy ||
                    paymentEntitlementLoading ||
                    premiumPlusActive ||
                    premiumPlusIncluded
                  }
                  onClick={() =>
                    (window.location.pathname.startsWith("/profile-preview") ? window.location.assign("/profile-preview/checkout.html?plan=premium-plus") : startPaidCheckout("subscription", "premium_plus"))
                  }
                >
                  {paymentCheckoutBusy === "subscription-premium_plus"
                    ? "Apertura Stripe..."
                    : premiumPlusActive
                      ? "Premium Plus attivo"
                      : premiumPlusIncluded
                        ? "Incluso nel tuo piano"
                        : activePremiumRank === 1
                          ? "Passa a Premium Plus"
                          : "Attiva Premium Plus"}
                </button>
              </div>

              <div className="rounded-xl border border-edge bg-white p-4 flex h-full flex-col">
                <div className="font-semibold">Creator Pro</div>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="rounded-full border border-edge px-4 py-2 text-sm font-semibold hover:bg-cream"
                      onClick={() =>
                        window.location.assign(
                          "/checkout.html?plan=creator-pro"
                        )
                      }
                    >
                      Scopri dettagli
                    </button>
                  </div>

                  {paidServiceDetailsOpen === "creator_pro" && (
                    <div className="mt-3 rounded-xl border border-edge bg-cream/60 p-4 text-sm text-muted">
                      <div className="font-semibold text-ink">
                        Cosa include Creator Pro
                      </div>
                      <p className="mt-2">
                        Comprende anche le funzioni di Premium Start
                        e Premium Plus ed è pensato per chi pubblica
                        e monetizza contenuti.
                      </p>
                      <p className="mt-2">
                        Include strumenti Creator, statistiche
                        avanzate, modifica vocale professionale,
                        maggiore capacità di archiviazione,
                        caricamenti video più pesanti e funzioni
                        per i contenuti a pagamento.
                      </p>
                    </div>
                  )}
                <div className="mt-1 text-2xl font-bold text-primary">
                  € 7,99
                  <span className="text-sm font-normal text-muted">
                    {" "}/ mese
                  </span>
                </div>

                <div className="mt-2 text-xs text-muted">
                  Tutte le funzioni di Premium Plus
                  <br />
                  Strumenti Creator
                  <br />
                  Statistiche e gestione dei contenuti
                </div>

                <button
                  type="button"
                  className="mt-auto w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={
                    !!paymentCheckoutBusy ||
                    paymentEntitlementLoading ||
                    creatorProActive
                  }
                  onClick={() =>
                    (window.location.pathname.startsWith("/profile-preview") ? window.location.assign("/profile-preview/checkout.html?plan=creator-pro") : startPaidCheckout("subscription", "creator_pro"))
                  }
                >
                  {paymentCheckoutBusy === "subscription-creator_pro"
                    ? "Apertura Stripe..."
                    : creatorProActive
                      ? "Creator Pro attivo"
                      : activePremiumRank > 0
                        ? "Passa a Creator Pro"
                        : "Attiva Creator Pro"}
                </button>
              </div>
            </div>
          </div>

          
          {adsCheckoutConfirm && (
            <div
              className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/55 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ads-checkout-confirm-title"
              data-testid="ads-checkout-confirm-modal"
            >
              <div className="w-full max-w-lg rounded-2xl border border-edge bg-white p-6 shadow-2xl">
                <div className="text-center">
                  <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-primary">
                    <Share2 size={21} />
                  </div>

                  <h3
                    id="ads-checkout-confirm-title"
                    className="font-display text-2xl font-semibold"
                  >
                    {adsCheckoutPackages[adsCheckoutConfirm]?.name}
                  </h3>

                  <p className="mt-2 text-sm text-muted">
                    Controlla il pacchetto selezionato prima di continuare al pagamento.
                  </p>
                </div>

                <div className="mt-5 overflow-hidden rounded-xl border border-edge bg-cream/40">
                  <div className="flex items-center justify-between border-b border-edge px-4 py-3">
                    <span className="text-sm text-muted">Prezzo</span>
                    <strong className="text-base">
                      € {String(adsCheckoutPackages[adsCheckoutConfirm]?.price || "").replace(".", ",")}
                    </strong>
                  </div>

                  <div className="flex items-center justify-between border-b border-edge px-4 py-3">
                    <span className="text-sm text-muted">Durata</span>
                    <strong className="text-sm">
                      {adsCheckoutPackages[adsCheckoutConfirm]?.days} giorni
                    </strong>
                  </div>

                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-muted">Copertura indicativa</span>
                    <strong className="text-sm text-right">
                      {adsCheckoutPackages[adsCheckoutConfirm]?.coverage}
                    </strong>
                  </div>
                </div>

                <p className="mt-4 text-xs leading-relaxed text-muted">
                  Confermando verrai indirizzato al pagamento sicuro Stripe.
                  Il pacchetto acquistato sarà quello indicato in questo riepilogo.
                </p>

                {paymentCheckoutMessage && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {paymentCheckoutMessage}
                  </div>
                )}

                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    className="rounded-full border border-edge bg-white px-5 py-3 text-sm font-semibold hover:bg-cream disabled:opacity-50"
                    disabled={Boolean(paymentCheckoutBusy)}
                    onClick={() => setAdsCheckoutConfirm(null)}
                  >
                    Torna ai pacchetti
                  </button>

                  <button
                    type="button"
                    className="rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
                    disabled={Boolean(paymentCheckoutBusy)}
                    onClick={() =>
                      startPaidCheckout("ads", adsCheckoutConfirm)
                    }
                  >
                    {paymentCheckoutBusy
                      ? "Apertura pagamento Stripe..."
                      : "Conferma e vai al pagamento"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {paymentCheckoutMessage && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {paymentCheckoutMessage}
            </div>
          )}
        </section>
          )}
        </>
      )}


      {isOwner && (
        <section
          className="card mb-5 p-4"
          data-testid="profile-payment-methods"
        >
          <h3 className="text-lg font-semibold">
            Metodi di pagamento
          </h3>

          <p className="mt-1 text-sm text-muted">
            Scegli come pagare abbonamenti Premium, Creator e altri servizi You Voice.
          </p>

          <p className="mt-3 rounded-xl border border-edge bg-cream px-3 py-2 text-xs">
            Il pagamento viene gestito dal checkout sicuro Stripe.
            I metodi effettivamente disponibili dipendono dal dispositivo,
            dal browser e dalla configurazione Stripe.
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-[28px] border border-edge bg-white px-4 py-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="inline-flex items-center rounded-md border border-edge bg-white px-2 py-1 text-[11px] font-extrabold tracking-wide text-blue-700">
                  VISA
                </span>

                <span className="inline-flex items-center gap-1 rounded-md border border-edge bg-white px-2 py-1 text-[10px] font-semibold text-neutral-700">
                  <span className="inline-block h-3 w-3 rounded-full bg-red-500" />
                  <span className="-ml-1 inline-block h-3 w-3 rounded-full bg-orange-400" />
                  <span>Mastercard</span>
                </span>
              </div>

              <div className="font-semibold">Carte</div>
              <div className="text-xs text-muted">
                Visa e Mastercard
              </div>
            </div>

            <div className="rounded-[28px] border border-edge bg-white px-4 py-4">
              <div className="mb-3">
                <span className="inline-flex items-center rounded-md border border-edge px-3 py-1.5 text-sm font-extrabold text-blue-700">
                  PayPal
                </span>
              </div>

              <div className="font-semibold">PayPal</div>
              <div className="text-xs text-muted">
                Se disponibile nel checkout
              </div>
            </div>

            <div className="rounded-[28px] border border-edge bg-white px-4 py-4">
              <div className="mb-3">
                <span className="inline-flex items-center rounded-md border border-edge px-3 py-1.5 text-sm font-semibold text-black">
                   Pay
                </span>
              </div>

              <div className="font-semibold">Apple Pay</div>
              <div className="text-xs text-muted">
                Su dispositivi compatibili
              </div>
            </div>

            <div className="rounded-[28px] border border-edge bg-white px-4 py-4">
              <div className="mb-3">
                <span className="inline-flex items-center gap-0.5 rounded-md border border-edge px-3 py-1.5 text-sm font-semibold">
                  <span className="text-blue-600">G</span>
                  <span className="text-neutral-900">Pay</span>
                </span>
              </div>

              <div className="font-semibold">Google Pay</div>
              <div className="text-xs text-muted">
                Su dispositivi compatibili
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl bg-cream p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted">
              Metodo predefinito
            </div>
            <div className="mt-1 font-semibold">
              Gestito nel checkout Stripe
            </div>
          </div>
        </section>
      )}

      

      </div>
    </div>
  );
}

function ProfileHeader({ onBack }) {
  return (
    <div className="border-b border-edge bg-cream sticky top-0 z-30">
      <div
        className={
          `"w-full max-w-[1180px] mx-auto px-[18px]" h-14 flex items-center justify-between`
        }
      >
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest hover:text-primary" data-testid="profile-back-btn">
          <ChevronLeft size={14} /> Home
        </button>
        <span className="font-display font-semibold text-lg">Profilo</span>
        <span className="w-[18px]" aria-hidden="true" />
      </div>
    </div>
  );
}

function ProfileHeaderCard({ user, isOwner, premiumFrame = false, customHumanAvatar = false }) {
  // YOUVOICE_PROFILE_HEADER_AVATAR_LOCAL_STATE_V1
  const [displayAvatar, setDisplayAvatar] = useState(user?.avatar || "");

  useEffect(() => {
    setDisplayAvatar(user?.avatar || "");
  }, [user?.avatar, user?.identityId]);

  const DEFAULT_PROFILE_BIO = "Esprimo le mie opinioni, condivido vocali, immagini e video. Questo spazio raccoglie tutti i miei contenuti pubblici su You Voice.";

  const savedBio =
    typeof user?.bio === "string" && user.bio.trim()
      ? user.bio.trim()
      : DEFAULT_PROFILE_BIO;

  const [bioEditing, setBioEditing] = useState(false);
  const [bioDraft, setBioDraft] = useState(savedBio);
  const [bioLocal, setBioLocal] = useState(null);
  const [bioBusy, setBioBusy] = useState(false);
  const [bioMessage, setBioMessage] = useState("");

  const visibleBio = bioLocal ?? savedBio;

  const startBioEdit = () => {
    setBioDraft(visibleBio);
    setBioMessage("");
    setBioEditing(true);
  };

  const cancelBioEdit = () => {
    setBioDraft(visibleBio);
    setBioMessage("");
    setBioEditing(false);
  };

  const saveBio = async () => {
    if (bioBusy) return;

    const nextBio = bioDraft.trim();

    if (nextBio.length > 500) {
      setBioMessage("La bio non può superare 500 caratteri.");
      return;
    }

    try {
      setBioBusy(true);
      setBioMessage("Salvataggio bio...");

      const saved = await updateProfileBio({
        bio: nextBio || null,
      });

      const persistedBio =
        typeof saved?.bio === "string" && saved.bio.trim()
          ? saved.bio.trim()
          : DEFAULT_PROFILE_BIO;

      setBioLocal(persistedBio);
      setBioDraft(persistedBio);
      setBioEditing(false);
      setBioMessage("Bio salvata.");
    } catch (error) {
      setBioMessage(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Impossibile salvare la bio."
      );
    } finally {
      setBioBusy(false);
    }
  };

  const [coverObjectKey, setCoverObjectKey] = useState(
    user.coverObjectKey || ""
  );
  const [coverKind, setCoverKind] = useState(
    user.coverKind || ""
  );
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverMessage, setCoverMessage] = useState("");

  const [premiumAvatarOpen, setPremiumAvatarOpen] = useState(false);
  const [premiumAvatarGender, setPremiumAvatarGender] = useState("male");
  const [premiumAvatarSkin, setPremiumAvatarSkin] = useState("#f3c7a5");
  const [premiumAvatarHair, setPremiumAvatarHair] = useState("#201813");
  const [premiumAvatarEye, setPremiumAvatarEye] = useState("#36566e");
  const [premiumAvatarPreview, setPremiumAvatarPreview] = useState("");
  const [premiumAvatarBlob, setPremiumAvatarBlob] = useState(null);
  const [premiumAvatarBusy, setPremiumAvatarBusy] = useState(false);
  const [premiumAvatarMessage, setPremiumAvatarMessage] = useState("");

  useEffect(() => {
    setCoverObjectKey(user.coverObjectKey || "");
    setCoverKind(user.coverKind || "");
  }, [user.coverObjectKey, user.coverKind]);


  const buildPremiumAvatarPreview = async () => {
    if (!isOwner || !customHumanAvatar || premiumAvatarBusy) return;

    setPremiumAvatarBusy(true);
    setPremiumAvatarMessage("");

    try {
      // YOUVOICE_PREMIUM_PHOTOREAL_AVATAR_V3
      const generatedUrl = await generateAiAvatar(
        premiumAvatarGender,
        {
          skin: premiumAvatarSkin,
          hair: premiumAvatarHair,
          eye: premiumAvatarEye,
        }
      );

      const generatedResponse = await fetch(generatedUrl, {
        credentials: "include",
      });

      if (!generatedResponse.ok) {
        throw new Error("Impossibile recuperare l'avatar AI generato.");
      }

      const generatedBlob = await generatedResponse.blob();

      if (!(generatedBlob instanceof Blob) || generatedBlob.size <= 0) {
        throw new Error("Avatar AI generato non valido.");
      }

      setPremiumAvatarPreview(generatedUrl);
      setPremiumAvatarBlob(generatedBlob);
      setPremiumAvatarMessage("Anteprima pronta.");
    } catch (error) {
      console.error("Avatar Premium: anteprima fallita", error);
      setPremiumAvatarMessage(
        error?.message || "Impossibile generare l'anteprima."
      );
    } finally {
      setPremiumAvatarBusy(false);
    }
  };

  const savePremiumHumanAvatar = async () => {
    if (
      !isOwner ||
      !customHumanAvatar ||
      !premiumAvatarPreview ||
      premiumAvatarBusy
    ) {
      return;
    }

    setPremiumAvatarBusy(true);
    setPremiumAvatarMessage("Salvataggio avatar...");

    try {
      // YOUVOICE_PREMIUM_AVATAR_DIRECT_SAVE_V1
      const avatarUrl = premiumAvatarPreview;

      if (!avatarUrl) {
        throw new Error("Avatar Premium non disponibile.");
      }

      // YOUVOICE_PREMIUM_AVATAR_PROFILE_STATE_V1
      const savedProfile = await upsertProfile({
        displayName: user?.name || "Utente",
        avatar: avatarUrl,
      });

      setDisplayAvatar(savedProfile?.avatar || avatarUrl);

      setPremiumAvatarMessage("Avatar Premium salvato e aggiornato.");
    } catch (error) {
      console.error("Avatar Premium: salvataggio fallito", error);

      setPremiumAvatarMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Impossibile salvare l'avatar."
      );
    } finally {
      setPremiumAvatarBusy(false);
    }
  };

  const mediaUrlFor = (objectKey) => {
    if (!objectKey) return "";

    const safeKey = String(objectKey)
      .split("/")
      .map((part) => encodeURIComponent(part))
      .join("/");

    return `/api/v1/uploads/media/${safeKey}`;
  };

  const coverUrl = mediaUrlFor(coverObjectKey);

  const handleCoverSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file || coverBusy) return;

    setCoverBusy(true);
    setCoverMessage(
      "Caricamento e moderazione della copertina..."
    );

    try {
      const attachment = await uploadPostMedia(file);

      const kind =
        attachment?.kind ||
        (file.type.startsWith("video/")
          ? "video"
          : "image");

      const moderation =
        await requestProfileCoverModeration(
          attachment.objectKey,
          kind
        );

      const status = String(
        moderation?.status ||
        moderation?.moderationStatus ||
        ""
      ).toLowerCase();

      if (status !== "approved") {
        throw new Error(
          status === "rejected"
            ? "La copertina non è stata approvata."
            : "La moderazione non è ancora completata."
        );
      }

      const saved = await updateProfileCover({
        objectKey: attachment.objectKey,
        kind,
        moderationStatus: "approved",
      });

      setCoverObjectKey(
        saved?.coverObjectKey ||
        attachment.objectKey
      );

      setCoverKind(
        saved?.coverKind ||
        kind
      );

      setCoverMessage("Copertina salvata.");
    } catch (error) {
      setCoverMessage(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Impossibile salvare la copertina."
      );
    } finally {
      setCoverBusy(false);
      event.target.value = "";
    }
  };

  const resetCover = async () => {
    if (coverBusy) return;

    setCoverBusy(true);
    setCoverMessage("Rimozione copertina...");

    try {
      await updateProfileCover({
        objectKey: null,
        kind: null,
        moderationStatus: null,
      });

      setCoverObjectKey("");
      setCoverKind("");
      setCoverMessage("Copertina rimossa.");
    } catch (error) {
      setCoverMessage(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Impossibile rimuovere la copertina."
      );
    } finally {
      setCoverBusy(false);
    }
  };

  return (
    <div
      className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm"
      data-testid="profile-header-card"
    >
      <div
        className="relative min-h-[190px] overflow-hidden bg-[#b5121b] sm:min-h-[220px]"
        data-testid="profile-cover"
      >
        {coverUrl && coverKind === "image" && (
          <img
            src={coverUrl}
            alt="Copertina profilo"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        {coverUrl && coverKind === "video" && (
          <video
            src={coverUrl}
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          />
        )}

        {!coverUrl && (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-black/5 via-transparent to-black/20" />

            <div className="relative flex min-h-[190px] items-start justify-between p-5 sm:min-h-[220px] sm:p-7">
              <img
                src="/you-voice-brand.png"
                alt="You Voice"
                className="w-[170px] max-w-[58%] object-contain drop-shadow-md sm:w-[220px]"
              />

              <div
                className="select-none text-right text-xl tracking-[0.32em] text-yellow-300 drop-shadow sm:text-2xl"
                aria-hidden="true"
              >
                ★★★
              </div>
            </div>
          </>
        )}

        {isOwner && (
          <div
            className="absolute bottom-3 right-3 z-20 flex flex-wrap justify-end gap-2"
            data-testid="profile-cover-controls"
          >
            <input
              id="profile-cover-input"
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleCoverSelected}
              disabled={coverBusy}
            />

            <label
              htmlFor="profile-cover-input"
              className={`rounded-full border border-white/70 bg-black/60 px-4 py-2 text-xs font-semibold text-white shadow backdrop-blur-sm ${
                coverBusy
                  ? "cursor-wait opacity-60"
                  : "cursor-pointer hover:bg-black/75"
              }`}
            >
              {coverBusy
                ? "Attendi..."
                : coverUrl
                ? "Sostituisci copertina"
                : "Modifica copertina"}
            </label>

            {coverUrl && (
              <button
                type="button"
                onClick={resetCover}
                disabled={coverBusy}
                className="rounded-full border border-white/70 bg-black/60 px-4 py-2 text-xs font-semibold text-white shadow backdrop-blur-sm hover:bg-black/75 disabled:opacity-60"
              >
                Elimina copertina
              </button>
            )}
          </div>
        )}
      </div>

      <div className="relative px-5 pb-5 sm:px-7 sm:pb-7">
        <div className="-mt-14 mb-3">
          <img
            src={displayAvatar || user?.avatar || ""}
            alt=""
            className={`h-28 w-28 rounded-full border-4 bg-white object-cover shadow-md sm:h-32 sm:w-32 ${
              premiumFrame
                ? "border-yellow-400 ring-4 ring-yellow-200/70"
                : "border-white"
            }`}
          />
        </div>

        <h2 className="font-display text-3xl font-semibold leading-tight text-slate-950">
          {user.name}
        </h2>

        <div className="mt-1 text-sm text-muted">
          @{user.name.replace(/\s+/g, "").toLowerCase()}
        </div>

        
        {isOwner && customHumanAvatar && (
          <div
            className="mt-4 max-w-2xl rounded-2xl border border-yellow-200 bg-yellow-50/60 p-4"
            data-testid="premium-human-avatar-editor"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">
                  Avatar umano personalizzabile
                </div>
                <div className="mt-1 text-xs text-muted">
                  Funzione Premium Plus e Creator Pro
                </div>
              </div>

              <button
                type="button"
                className="action-btn"
                onClick={() => setPremiumAvatarOpen((value) => !value)}
                data-testid="premium-avatar-toggle-btn"
              >
                {premiumAvatarOpen ? "Chiudi" : "Personalizza avatar"}
              </button>
            </div>

            {premiumAvatarOpen && (
              <div className="mt-4 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="text-xs font-semibold">
                    Genere
                    <select
                      className="mt-1 w-full rounded-xl border border-edge bg-white px-3 py-2 text-sm"
                      value={premiumAvatarGender}
                      onChange={(event) =>
                        setPremiumAvatarGender(event.target.value)
                      }
                    >
                      <option value="male">Uomo</option>
                      <option value="female">Donna</option>
                    </select>
                  </label>

                  <label className="text-xs font-semibold">
                    Tonalità pelle
                    <select
                      className="mt-1 w-full rounded-xl border border-edge bg-white px-3 py-2 text-sm"
                      value={premiumAvatarSkin}
                      onChange={(event) =>
                        setPremiumAvatarSkin(event.target.value)
                      }
                    >
                      <option value="#f3c7a5">Chiara</option>
                      <option value="#d99b73">Media chiara</option>
                      <option value="#b87550">Media</option>
                      <option value="#7d4935">Scura</option>
                    </select>
                  </label>

                  <label className="text-xs font-semibold">
                    Capelli
                    <select
                      className="mt-1 w-full rounded-xl border border-edge bg-white px-3 py-2 text-sm"
                      value={premiumAvatarHair}
                      onChange={(event) =>
                        setPremiumAvatarHair(event.target.value)
                      }
                    >
                      <option value="#201813">Nero</option>
                      <option value="#4a2b1b">Castano scuro</option>
                      <option value="#7b4a27">Castano</option>
                      <option value="#d1a35d">Biondo</option>
                      <option value="#4b4b52">Grigio</option>
                    </select>
                  </label>

                  <label className="text-xs font-semibold">
                    Occhi
                    <select
                      className="mt-1 w-full rounded-xl border border-edge bg-white px-3 py-2 text-sm"
                      value={premiumAvatarEye}
                      onChange={(event) =>
                        setPremiumAvatarEye(event.target.value)
                      }
                    >
                      <option value="#2e2119">Marroni</option>
                      <option value="#36566e">Azzurri</option>
                      <option value="#436244">Verdi</option>
                      <option value="#6b4a2d">Nocciola</option>
                    </select>
                  </label>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="action-btn"
                    onClick={buildPremiumAvatarPreview}
                    disabled={premiumAvatarBusy}
                    data-testid="premium-avatar-preview-btn"
                  >
                    {premiumAvatarBusy
                      ? "Attendi..."
                      : "Genera anteprima"}
                  </button>

                  {premiumAvatarPreview && (
                    <>
                      <img
                        src={premiumAvatarPreview}
                        alt="Anteprima avatar Premium"
                        className="h-20 w-20 rounded-full border-4 border-yellow-400 bg-white object-cover shadow-md"
                      />

                      <button
                        type="button"
                        className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                        onClick={savePremiumHumanAvatar}
                        disabled={!premiumAvatarBlob || premiumAvatarBusy}
                        data-testid="premium-avatar-save-btn"
                      >
                        Salva nel profilo
                      </button>
                    </>
                  )}
                </div>

                {premiumAvatarMessage && (
                  <div
                    className="text-xs text-muted"
                    role="status"
                    data-testid="premium-avatar-message"
                  >
                    {premiumAvatarMessage}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

<div
        className="mt-4 max-w-2xl"
        data-testid="profile-bio"
      >
        {bioEditing && isOwner ? (
          <>
            <textarea
              value={bioDraft}
              onChange={(event) => setBioDraft(event.target.value)}
              maxLength={500}
              rows={4}
              className="w-full rounded-xl border border-slate-300 bg-white p-3 text-sm leading-6 text-slate-700 outline-none focus:border-slate-500"
              aria-label="Modifica bio"
            />

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={saveBio}
                disabled={bioBusy}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                {bioBusy ? "Salvataggio..." : "Salva"}
              </button>

              <button
                type="button"
                onClick={cancelBioEdit}
                disabled={bioBusy}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"
              >
                Annulla
              </button>

              <span className="text-xs text-muted">
                {bioDraft.length}/500
              </span>
            </div>
          </>
        ) : (
          <div className="flex items-start gap-2">
            <p className="flex-1 text-sm leading-6 text-slate-700">
              {visibleBio}
            </p>

            {isOwner && (
              <button
                type="button"
                onClick={startBioEdit}
                className="shrink-0 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700"
              >
                Modifica
              </button>
            )}
          </div>
        )}

        {bioMessage && isOwner && (
          <div className="mt-2 text-xs text-muted">
            {bioMessage}
          </div>
        )}
      </div>

        {coverMessage && isOwner && (
          <div className="mt-3 text-xs text-muted">
            {coverMessage}
          </div>
        )}

        {user.identityId && (
          <div
            className="mt-4 flex items-center gap-1 font-mono text-[10px] text-muted"
            data-testid="identity-id"
          >
            <KeyRound size={10} />
            ID: {user.identityId}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileStats({ analytics, loading }) {
  const stats = [
    {
      key: "followers",
      label: "Chi mi segue",
      value: analytics?.followers ?? 0,
      icon: Users,
    },
    {
      key: "following",
      label: "Persone seguite",
      value: analytics?.following ?? 0,
      icon: UserCheck,
    },
    {
      key: "posts",
      label: "Pubblicazioni",
      value: analytics?.posts ?? 0,
      icon: FileText,
    },
    {
      key: "likes",
      label: "Mi piace ricevuti",
      value: analytics?.likesReceived ?? 0,
      icon: Heart,
    },
    {
      key: "dislikes",
      label: "Non mi piace ricevuti",
      value: analytics?.dislikesReceived ?? 0,
      icon: ThumbsDown,
    },
    {
      key: "shares",
      label: "Diffusioni dei tuoi post",
      value: analytics?.sharesReceived ?? 0,
      icon: Share2,
    },
  ];

  return (
    <div
      className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
      aria-busy={loading}
    >
      {stats.map((stat) => {
        const Icon = stat.icon;

        return (
          <div
            key={stat.key}
            className="flex min-h-[150px] min-w-0 flex-col items-center justify-center rounded-2xl border border-red-200 bg-white px-2 py-4 text-center shadow-sm"
            data-testid={`profile-stat-${stat.key}`}
          >
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-primary">
              <Icon size={21} strokeWidth={2.2} />
            </div>

            <div className="font-display text-2xl font-bold leading-none text-ink">
              {loading ? "–" : stat.value}
            </div>

            <div className="mt-2 flex min-h-[36px] items-center justify-center text-[12px] font-semibold leading-[1.25] text-muted">
              {stat.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProfileNavRow({
  onOpenMessages,
  onLogout,
  isOwner = false,
  profileIdentityId = null,
}) {
  // YOUVOICE_VISITOR_DIRECT_MESSAGES_V1
  // Nel profilo di un altro utente mostriamo SOLO Messaggi privati
  // e apriamo direttamente la chat con il proprietario visitato.
  if (!isOwner) {
    return (
      <div className="mt-4 grid grid-cols-1 gap-2">
        <button
          className="card p-3 text-left hover:bg-cream"
          onClick={() => {
            if (
              profileIdentityId &&
              typeof onOpenMessages === "function"
            ) {
              onOpenMessages(String(profileIdentityId));
            }
          }}
          data-testid="open-messages-btn"
        >
          <MessageCircle size={18} className="text-primary" />
          <div className="text-sm font-semibold mt-1">
            Messaggi privati
          </div>
          <div className="text-[11px] text-muted">
            Apri le conversazioni
          </div>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button className="card p-3 text-left hover:bg-cream" onClick={onOpenMessages} data-testid="open-messages-btn">
          <MessageCircle size={18} className="text-primary" />
          <div className="text-sm font-semibold mt-1">Messaggi privati</div>
          <div className="text-[11px] text-muted">Apri le conversazioni</div>
        </button>
        <button className="card p-3 text-left hover:bg-cream" onClick={onLogout} data-testid="logout-btn">
          <LogOut size={18} className="text-primary" />
          <div className="text-sm font-semibold mt-1">Cambia identità</div>
          <div className="text-[11px] text-muted">Le 12 parole restano tue</div>
        </button>
      </div>
      {/* Zero-knowledge trust badge: reinforces the app's core promise so it
          becomes visible in the profile — a scelta architetturale visibile
          come value proposition. */}
      <div
        className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-1.5 text-[11px] font-medium"
        data-testid="zero-knowledge-badge"
        title="Le tue chiavi private non vengono mai inviate al server. Sei tu l'unico che può accedere ai tuoi messaggi."
      >
        <Lock size={12} strokeWidth={2.5} />
        <span>
          <span className="uppercase tracking-widest text-[10px] font-bold text-emerald-700">Zero-knowledge</span>
          <span className="mx-1.5 text-emerald-700/60">·</span>
          Le tue chiavi non lasciano mai questo dispositivo
        </span>
      </div>
    </>
  );
}

// ============ Moderazione ============
export function ModerationView({ onBack }) {
  return (
    <div className="min-h-screen bg-cream" data-testid="moderation-view">
      <ProfileHeader onBack={onBack} />
      <div className="max-w-3xl mx-auto px-6 py-6">
        <h2 className="font-display font-semibold text-4xl mb-1">Le mie segnalazioni</h2>
        <p className="text-sm text-muted mb-4">Qui compariranno soltanto segnalazioni ed esiti restituiti dal server.</p>
        <div className="card p-6 text-center text-muted text-sm" data-testid="moderation-empty">
          Nessuna segnalazione disponibile.
        </div>
      </div>
    </div>
  );
}
